import type { AgentField, AgentScreen, CodeLists } from './screenIndex';
import { codeListFor } from './screenIndex';
import type { AgentSkill, EditWindow } from './skills';
import { describeSkills, skillRowsAt } from './skills';
import { describeCapabilityTool, validateCapabilityPlan } from './capabilities';
import type { CapabilityTool } from './capabilities';
import type { UploadedFile } from './upload';
import { describeUploads } from './upload';

/**
 * 어느 단계의 호출인지 게이트웨이에 알린다.
 *
 * 모델 이름이 아니라 **단계 이름**을 보낸다. 어느 모델·어느 추론 강도로 푸는지는
 * 서버 설정이 정한다 — 브라우저가 모델을 고를 수 있으면 아무나 제일 비싼 모델을
 * 꽂아 하루 한도를 태울 수 있다.
 *
 * 계획과 계산이 같은 모델을 쓸 이유도 없다. 싼 모델은 코드 생성은 잘하고 계획에서
 * 약하다 — 없는 칸 이름을 짓거나 목록에 없는 코드값을 내는 것이 전부 계획 자리다.
 */
export type ChatOptions = {
  step?: 'plan' | 'compute' | 'answer' | 'howto' | 'task' | 'extract' | 'review' | 'reason';
  /**
   * 사람이 이 대화에 올린 파일. 이 호출의 사용자 메시지에 함께 실려 나간다.
   *
   * **파일이 없으면 이 칸도 없다.** 게이트웨이는 빈 배열이 아니라 없음을 보고 옛
   * 모양(문자열 한 덩이)을 그대로 쓴다 — 잘 돌던 길을 파일 기능이 건드리지 않는다.
   */
  files?: UploadedFile[];
};
export type Chat = (prompt: string, options?: ChatOptions) => Promise<string>;

/**
 * 답변을 흘려 받는다. `onDelta` 는 조각이 아니라 **지금까지 온 전체**를 받는다 —
 * 이어 붙이는 일을 보는 쪽마다 다시 하게 하지 않는다. 끝나면 그 전체를 돌려준다.
 */
export type ChatStream = (
  prompt: string,
  onDelta: (text: string) => void,
  options?: ChatOptions,
) => Promise<string>;

/**
 * 질문에 맞는 화면 경로를 좋은 순서로 돌려준다.
 *
 * 런타임은 브라우저에서 돌고 지식베이스는 서버에 있다. 검색 자격증명을
 * 브라우저에 두지 않으려고 함수로 받는다. 없으면 이름 겹침으로 떨어진다.
 */
export type Shortlist = (question: string) => Promise<string[]>;
export type DataRequirements = {
  dimensions: string[];
  measures: string[];
  operations: string[];
  artifact?: 'none' | 'xlsx' | 'csv' | 'docx' | 'pdf' | 'pptx';
};
export type Retrieval = {
  label: string;
  route: string;
  rowsStateKey: string;
  conditions: Record<string, string>;
  /** 계산에 반드시 필요한, 선택한 표의 실제 컬럼 id */
  requiredColumns?: string[];
  /**
   * **위 표 행마다 이 상세 표를 펼쳐 읽는다.** 일근태등록은 위 표가 날짜, 아래 표가 그날 직원별 출근이라
   * 기간 전체를 보려면 날마다 읽어야 한다. `from`·`to` 는 위 표 범위 칸(`master.rangeKey`) 값이다.
   */
  sweep?: {
    from?: string; to?: string;
    /**
     * **앞선 조회가 뽑은 행만 펼친다.** 그 조회의 `column` 칸 값과 위 표 `key` 칸 값이 같은 행만 고른다 —
     * 사원명부에서 뽑은 최근 입사자의 사번으로 인사등록 경력 탭을 읽는다(2026-10-21). 두 표의 칸 이름이
     * 다를 수 있다 — 사원명부 `perid` 와 인사등록 목록 `perid2` 가 같은 사번이다. `column` 이 없으면 `key`.
     */
    matchFrom?: { label: string; key: string; column?: string };
  };
};
/**
 * 여러 번 저장하는 일의 한 걸음.
 *
 * 걸음 하나는 지금 `prepare` 하나와 같은 힘을 갖는다. 새 개념을 만들지 않는다.
 */
export type SequenceStep = {
  /** 이 걸음 전에 열 탭 이름. 없으면 지금 탭 그대로 둔다. */
  tab?: string;
  /** 머리 칸. `prepare` 의 `fields` 와 같은 뜻이다. */
  fields?: Record<string, string>;
  /** 표에 넣을 행. */
  rows?: {
    stateKey: string;
    items: Record<string, string>[];
    /**
     * 행을 더하는 단추 이름. 기본은 `추가` 다.
     * 인사등록은 표마다 단추가 있지 않고 **화면의 `신규`** 가 지금 탭에 한 줄을 붙인다.
     */
    addLabel?: string;
  };
  /** 이 걸음에서 저장을 누르는가. */
  save: boolean;
  /** 사람이 읽을 이름. 실패했을 때 **어디까지 됐는지**를 이 말로 적는다. */
  label: string;
};

/**
 * 계획은 세 갈래다. **판별 합집합이라 갈래를 보지 않고 `retrievals` 를 읽을 수 없다** —
 * 되묻기 계획에는 그 칸이 아예 없고, 있는 척하면 그것을 읽는 쪽이 조용히 undefined 를 만난다.
 */
export type QueryPlan =
  | {
    kind: 'retrieve'; retrievals: Retrieval[]; reason: string; requirements?: DataRequirements;
    /** 검증을 풀고 채택한 계획이면 무엇을 못 지켰는지. 답변이 그 사실을 밝힌다. */
    relaxed?: string;
  }
  | { kind: 'prepare'; route: string; fields: Record<string, string>; reason: string;
    /**
     * 저장 뒤 결재상신까지 — 사람이 올리라고 말했을 때만. 신규를 쓴 날 82~95%가 같은 날 상신했다
     * (사용 기록 2026-09-29~10-13). 사용자 결정(10/13)으로 열었다.
     */
    submit?: boolean;
    /** 달력 화면에서 먼저 열 날짜(여덟 자리). 스케쥴관리는 그 날을 눌러야 쓸 자리가 생긴다. */
    calendarDay?: string; reuse?: {
    rowsStateKey: string; match: Record<string, string>; fields: string[];
  };
    /**
     * 헤더만으로는 쓸모없는 문서의 상세행. 견적서에 품목이 없으면 자재청구로 잇지도
     * 못한다 — 화면이 `조회된 자료가 없습니다` 로 막는다(2026-08-12 실측).
     */
    rows?: { stateKey: string; items: Record<string, string>[]; tab?: string; addLabel?: string };
    /**
     * 빈 상세행을 걷어내서 행이 없어졌다는 표식. `requireDraftRows` 가 이것을 보고
     * 요구를 접는다 — 다시 「행을 내라」고 하면 모델은 또 빈 행을 낸다.
     */
    droppedEmptyRows?: boolean;
    /**
     * **이미 있는 문서를 고친다.** 새로 만들지 않는다.
     *
     * 고장완료가 그렇다 — 접수는 이미 있고, 그 행을 골라 처리결과를 채워 저장한다.
     * 새로 만들면 접수가 하나 더 생길 뿐 완료되지 않는다.
     */
    edit?: {
      rowsStateKey: string; match: Record<string, string>; cells?: Record<string, string>;
      /** 칸을 고칠 표가 문서를 고르는 표와 다를 때. 출장보고서의 상세가 그렇다. */
      cellsStateKey?: string; cellsMatch?: Record<string, string>;
      /** 고른 문서 안의 표에서 한 줄 더 — 보수현장의 호기(`units`). */
      then?: { stateKey: string; match: Record<string, string> };
      /** 고칠 문서를 불러오는 조회 조건. 기본은 대개 이번 달이라 지난달 것은 못 찾는다. */
      conditions?: Record<string, string>;
      /** 되묻기에서 사용자가 고른 행 이름 그대로. 런타임이 채운다. */
      pick?: string;
      /** 헤더 칸을 표 칸보다 먼저 채운다. 스킬의 `fieldsBeforeCells` 가 건다. */
      fieldsFirst?: boolean;
      /**
       * 칸을 확정에서 바꾼다 — 고르는 순간 반영되는 칸이다. 스킬의 `deferCells` 가 건다.
       * 준비는 그 행까지만 짚는다.
       */
      deferCells?: boolean;
      /**
       * 목록 줄에서 창을 열고 창에서 줄을 골라 둔다 — 창 단추는 확정이 누른다. 스킬의
       * `editWindow` 가 건다. `cellsStateKey` 는 창 안 표, `cellsMatch` 는 사람이 말한 창 안 줄이다.
       */
      window?: EditWindow;
    };
  }
  /**
   * 화면 고유 버튼으로 앞 문서를 다음 문서로 잇는다. 견적서의 `자재청구서` 버튼처럼
   * 액션 목록에 없는 길이다. **Auto 가 켜졌을 때만** 실행된다.
   */
  | {
    kind: 'operate'; route: string; button: string; confirm?: string; reason: string;
    /** 창이 요구하는 값. 견적서의 자재청구 창은 비고가 비면 되돌려보낸다. */
    fields?: Record<string, string>;
    /** 창 안 표에서 전부 고를 상태 키. 안 고르면 `선택을 하나이상 하십시요` 로 막힌다. */
    selectKey?: string;
    /**
     * **표 안에 달린 버튼일 때.** 이게 있으면 `button` 은 사람에게 보여 줄 이름이고,
     * 실제로 누르는 것은 그 행 그 칸의 버튼이다.
     *
     * 이 버튼들은 대개 글자가 없는 아이콘이라 이름으로 못 찾고, 행마다 하나씩이라
     * **어느 행인지**를 골라야 한다. `column` 은 `describe` 가 알려 준
     * `표.칸(이름)` 의 칸이다.
     */
    row?: {
      stateKey: string; match: Record<string, string>; column: string; nth?: number;
      /** 누르기 전에 걸 조회 조건. 표가 비면 고를 행이 없다. */
      conditions?: Record<string, string>;
    };
    /** 화면 목록을 이 조건으로 조회해 두고(check 면 행을 다 골라) 화면 버튼을 누른다. */
    list?: {
      stateKey: string; conditions?: Record<string, string>; check?: boolean;
      /** 체크 칸 이름. 기본 `chk` — 전자세금계산서발행은 `selchk` 다. */
      checkKey?: string;
      /** 이 값들로 시작하는 줄만 체크한다(거래처 이름 등). 없으면 목록 전부. */
      match?: Record<string, string>;
    };
    /** 누르기 전에 고를 목록 행 — 문서 단추(견적서복사·결재상신)는 화면이 쥔 그 문서에 일한다. */
    focus?: { stateKey: string; match: Record<string, string>; conditions?: Record<string, string>; then?: { stateKey: string; match: Record<string, string> } };
  }
  /**
   * 메일·문자·팩스·카카오톡. 화면 액션이 아니라 전역 모달을 거친다.
   * **Auto 가 켜졌을 때만** 나간다.
   */
  | {
    kind: 'send'; route: string; channel: 'email' | 'sms' | 'kakao' | 'fax';
    to?: string; subject?: string; body?: string; reason: string;
    /**
     * 전송 창 앞에 놓인 창이 요구하는 값.
     *
     * 고장접수의 `문자전송` 은 먼저 문자유형을 묻는다. 고르지 않으면 확인을 눌러도
     * 아무 일도 일어나지 않는다 — 보통 문자는 `{"gubun":"0"}` 다.
     */
    fields?: Record<string, string>;
    /**
     * 받는 사람의 번호·주소를 **어디서 찾을지**.
     *
     * 사람은 `덕봉빌딩 담당자한테 문자 보내줘` 라고 말하지 번호를 부르지 않는다.
     * 번호는 ERP 가 이미 들고 있다 — 되묻는 것은 아는 것을 모른 척하는 것이다.
     */
    toLookup?: { route: string; rowsStateKey: string; conditions: Record<string, string>; column: string };
    /**
     * 보낼 화면에서 **먼저 걸 조회 조건**.
     *
     * 고장접수의 문자는 화면에 조회된 고장내역을 보낸다. 조회 없이 누르면
     * `문자전송할 고장내역이 없습니다` 로 되돌아온다.
     */
    conditions?: Record<string, string>;
  }
  /**
   * **여러 번 저장하는 일.** 탭마다 저장이 갈리는 화면이 있다 — 인사등록은 기본정보를
   * 먼저 저장해야 사원번호가 생기고, 그 뒤에야 가족·경력이 붙는다. 계획이 문서 하나만
   * 내던 동안 그런 화면에는 아무것도 쓸 수 없었다. 문의의 43.8%가 탭 화면이다.
   */
  | { kind: 'sequence'; route: string; reason: string; steps: SequenceStep[] }
  /**
   * **파일을 문서에 붙인다.** 화면을 건드리지 않는다 — 앱이 이미 두 번 부르는 길을
   * 갖고 있다(`tempUpload` 다음 `attachment`). 첨부를 쓰는 화면이 45개다.
   */
  | { kind: 'attach'; route: string; reason: string }
  | { kind: 'howto'; route: string; reason: string }
  /**
   * **같은 일을 여러 건** — 날짜마다 점검 옮기기, 사람마다 당직 넣기처럼. 건마다 prepare 계획 하나.
   * 런타임이 목록을 보여 주고 한 번 확인받은 뒤 차례로 한다(사용자 결정 2026-10-13). 사람은 이런 일을
   * 줄줄이 묶어서 한다 — 점검계획 날짜 옮기기 한 묶음이 최대 143건, 입금처리 111건(사용 기록).
   */
  | { kind: 'batch'; reason: string; items: Extract<QueryPlan, { kind: 'prepare' }>[] }
  /**
   * 미결함(결재 대기)의 문서를 승인·반려·전결한다. 화면은 정해져 있다(`/w_tb_e080_list`). 무엇을
   * 결재할지는 런타임이 목록을 보여 주고 사람에게 한 번 확인받는다(사용자 결정 2026-10-13).
   */
  | {
    kind: 'approve';
    action: '승인' | '반려' | '전결';
    /** 문서를 가리는 말 — 제목·작성자·문서 종류(견적서·지출결의서…)의 일부. 없으면 미결함 전부. */
    match?: { title?: string; writer?: string; paper?: string };
    /** 반려 사유·지시사항. */
    remark?: string;
    reason: string;
  }
  | { kind: 'ask'; question: string; options?: string[]; reason: string;
      route?: string; field?: string };
export type PlanContext = {
  currentRoute?: string;
  openTabs?: { path: string; name: string }[];
  /**
   * 이 대화의 앞선 질문과 답변. 행 값은 들어가지 않는다.
   *
   * `routes` 는 그때 조회한 화면이다. **후보 목록에 다시 올리는 데 쓴다** — 이어지는
   * 질문("금액이 큰 현장별로 정리해줘")에는 화면을 가리키는 말이 없어서 검색이 앞
   * 화면을 못 찾고, 프롬프트는 "아래 목록에 있는 화면만 쓴다"고 말한다. 목록에 없으면
   * 고를 방법이 없다.
   */
  history?: { question: string; answer: string; routes?: string[] }[];
  me?: { perid: string; pernm: string; spjangcd?: string; spjangnm?: string };
  businessSites?: { code: string; name: string; current: boolean }[];
  /** 사용자가 맞다고 확인한, 값이 제거된 과거 실행 레시피. */
  experiences?: {
    question: string;
    recipe: {
      route: string;
      rowsStateKey: string;
      conditionShape: Record<string, string>;
      requiredColumns: string[];
    }[];
    /** 그때 실행이 실제로 자료를 냈는가. 없으면 모르는 것이고, 모르면 안 쓴다. */
    validation?: { valid: boolean; issues?: unknown[] };
  }[];
  /**
   * **레시피 확정.** 검증된 실행 경험이 이 물음과 겹치면 모델에 묻지 않고 그대로 쓴다.
   *
   * 같은 지시에 판마다 다른 화면으로 가던 것(스펙 §2.3 C)을 여기서 끊는다. 기본은
   * 꺼져 있다 — 스펙 §5.6 이 「오염 없는 5판으로 C 의 크기를 잰 뒤에 켠다」로 정했다.
   */
  confirmRecipes?: boolean;
  skills?: AgentSkill[];
  /**
   * 질문에 걸린 업무 용어의 원본 화면(`termRoutes`). 검색이 못 줘도 후보에 넣는다 — 「시제 체크해줘」에
   * 검색은 엉뚱한 열둘을 줬고 계획은 월시재현황을 고를 기회도 없었다(실제 테넌트, 2026-10-23).
   */
  termRoutes?: string[];
  /**
   * 고르지 않은 것까지 포함한 스킬 전부.
   *
   * 상세행 계약은 **화면에 붙은 사실**이지 선택의 결과가 아니다. 계획이 입고등록을
   * 골랐는데 뽑힌 스킬이 자재청구서의 것이면 그 화면의 계약은 아무도 안 본다 —
   * 자재 한 줄 없는 입고가 그대로 저장됐다. 화면이 같으면 계약도 같이 본다.
   */
  allSkills?: AgentSkill[];
  /**
   * 우리 되묻기에 답한 턴인가.
   *
   * 그 턴의 계획은 **고른 값 하나만** 낸다 — 헤더는 이미 화면에 채워져 있다.
   * 필수 칸을 다시 요구하면 답한 사람에게 같은 것을 또 내라고 하는 셈이다.
   */
  continuing?: boolean;
  /** TaskSpec으로 먼저 선택된 typed 업무 도구. */
  capability?: CapabilityTool;
  /**
   * 사람이 이번 턴에 올린 파일.
   *
   * **계획 호출에만 싣는다.** 값을 뽑는 것은 계획의 일이고, 계산·답변 단계가 같은
   * 그림을 다시 받아 봐야 토큰만 태운다 — 그림 한 장이 수천 토큰이다.
   */
  files?: UploadedFile[];
  /**
   * 올린 문서에서 **또렷하게 읽은 값**.
   *
   * 질문에 붙여 넘기는 것만으로는 안 된다 — 작업 구조화(`taskQuestion`)가 질문을 다시
   * 써서 그 글이 사라진다. 실측(2026-09-01): 문서에서 `입사일 2026-09-10` 을 0.99 로
   * 읽었는데 계획은 **오늘 날짜**를 넣고 부서·직위를 통째로 버렸다.
   */
  facts?: { label: string; value: string }[];
};

const SHORTLIST = 12;

/**
 * **프롬프트에 설명을 붙이는 화면 수.** 후보 목록 길이(`SHORTLIST`)와 일부러 갈랐다.
 *
 * `SHORTLIST` 는 `withRecent` 의 자르는 길이도 겸한다. 그걸 줄이면 최근에 연 화면이
 * 앞에 붙어 **검색 결과를 통째로 밀어낸다** — 한 세션에서 업무를 이어 할 때 새 물음의
 * 후보가 하나도 안 남는다. 그래서 목록은 그대로 두고 **설명을 붙이는 수만** 줄인다.
 *
 * 왜 줄이나 (실측 2026-09-21, 조회 29건)
 *
 * ```
 * 검색 서버 1등만 따르기   23/29    ← 모델을 안 부른다
 * 검색 서버 앞 3 안        28/29
 * 지금 런타임(모델이 고름)  21/29    ← 후보 12개에 전체 설명 8.7k 자
 * ```
 *
 * 모델은 **맞는 1등을 뒤집어서** 진다. 그리고 4번 아래 후보는 정답을 거의 안 담는다.
 */
const PROMPT_SCREENS = 12;

/**
 * 잣대가 다시 빌드하지 않고 A/B 하려고 두는 자리. 안 건드리면 `PROMPT_SCREENS` 그대로다.
 * 1 이면 **모델이 화면을 고르지 못한다** — 첫 후보 하나만 설명이 간다.
 */
function promptScreens(): number {
  const override = Number(
    (globalThis as { __elmanAgentPromptScreens?: unknown }).__elmanAgentPromptScreens,
  );
  return Number.isInteger(override) && override > 0 ? override : PROMPT_SCREENS;
}

/** 계획 프롬프트에 이미 화면 열두 개가 실려 있다. 이력까지 길어지면 화면이 밀린다. */
const HISTORY_TURNS = 6;

function isSelectionGrid(key: string): boolean {
  return /^openChk\d*$/i.test(key);
}

/**
 * 결과를 담을 수 있는 표인가.
 *
 * 컬럼이 하나뿐인 표는 대개 필터 목록이다 — 월별매출현황의 `openChk[매출구분]`
 * 이 그렇다. 그 화면은 결과 표를 정적으로 잡을 수 없어 필터만 남는데, 이름이
 * 질문과 잘 겹쳐 골라진 뒤 필터를 결과라고 내놓는다. 실제로 그렇게 나왔다.
 */
function usable(screen: AgentScreen): boolean {
  return screen.grids.some((grid) => !isSelectionGrid(grid.key)
    // **달력은 열이 없다.** 열 수로만 재면 달력 화면이 통째로 안 보인다 —
    // 점검계획등록·영업스케쥴·사전점검스케쥴·공사계획등록 넷이 그랬고, 그중 하나는
    // 문의 상위 12위다. 자료는 있고 드라이버에 달력을 여는 길도 있다.
    // **칸을 모르는 것과 읽을 것이 없는 것은 다르다.** 탭이 표를 바꾸는 화면은 카탈로그가
    // 칸을 정적으로 못 뽑아 `columns: []` 로 남는다(`header={scope.currentTabHeader}`).
    // 병합은 그럴 때 **관측이 실제로 본 배열 이름**을 `kind: 'observed'` 로 적어 둔다 —
    // 화면이 그 이름으로 자료를 그리는 것을 봤다는 뜻이다. 칸 수로만 재면 그런 화면이
    // 통째로 사라진다: 고장처리현황은 관측이 배열 일곱을 봤고 실제로 자료가 온다
    // (실측 2026-09-14). 표시가 없는 빈 표는 예전대로 뺀다 — 그건 정말 읽을 것이 없다.
    && (grid.kind === 'calendar' || grid.kind === 'observed' || grid.columns.length >= 2))
    || Boolean(screen.actions?.includes('new') && screen.draftFields?.length)
    // **표가 없는 양식 화면도 읽을 것이 있다.** 회사정보등록은 표 0개에 양식 칸 26개짜리
    // 설정 화면이라 `new` 가 없다 — 위 조건만으로는 영영 안 보인다. 「회사 정보 보여줘」는
    // 사람이 당연히 하는 말인데 모델이 고를 기회조차 없었다.
    || Boolean(screen.actions?.includes('retrieve') && screen.draftFields?.length);
}

/**
 * 프롬프트에 넣을 화면을 좁힌다.
 *
 * 282개를 전부 넣으면 프롬프트가 100 KB 를 넘는다. 질문의 글자와 화면 이름이
 * 겹치는 순으로 줄인다. 겹치는 것이 없어도 비우지 않는다 — 이름이 안 겹치는
 * 질문("작년 대비 얼마나 컸어")이 흔하고, 고를 기회는 줘야 한다.
 *
 * 표가 없는 화면은 조회해도 읽을 것이 없으므로 뺀다.
 */
/**
 * 사람이 쓰는 말과 화면 이름이 다른 자리를 잇는다.
 *
 * `수금 상황은 어때?` 가 아무 화면도 못 찾고 끝났다. 화면 이름은 `입금현황` 과
 * `미수금현황` 이다 — 글자로는 거의 안 겹치고, 뜻으로는 바로 그것이다. 검색 서버가
 * 이런 말을 잡아 주면 좋지만, 못 잡을 때 후보가 아예 비면 고를 기회 자체가 없다.
 *
 * 넓게 벌리지 않는다. 여기 적는 것은 이 회사 사람들이 실제로 쓰는 말과 화면 이름이
 * 어긋나는, 확인된 짝뿐이다.
 */
const SYNONYMS: Record<string, string[]> = {
  수금: ['입금', '미수금', '수납'],
  미수: ['미수금', '채권'],
  매입: ['구매', '발주'],
  매출: ['판매', '수주'],
  급여: ['임금', '보수'],
  재고: ['자재', '품목'],
  고장: ['장애', '수리'],
  계약: ['보수계약', '유지보수'],
  // 사람은 줄여 말하고 화면은 길게 적는다. **일반적인 말 차이만** 넣는다 — 잣대를
  // 통과시키려고 케이스에 맞춘 낱말을 넣기 시작하면 그건 사전이 아니라 정답표다.
  앱: ['어플', '어플리케이션'],
};

// **`연차 → 휴가` 는 넣지 않는다.** 「연차 하루 신청서」는 9등→1등이 되지만
// 「우리 회사 잔여 연차 평균」이 개인 휴가신청서로 간다 — 예전에 일부러 고쳐 둔
// 자리다(`aggregateScreens` 시험). 한쪽을 살리려 조건을 붙이면 그건 사전이 아니라
// 케이스에 맞춘 정답표가 된다. 연차 신청은 다른 길로 풀어야 한다.

function choiceOf(button: string, oneRow: boolean): string | undefined {
  // **창이 확인 대신 갈래 단추를 내미는 버튼**은 계획이 갈래를 빠뜨리면 창 앞에서 멈춘다. 보수현장
  // 정보센터연동은 「예 (일괄연동)」·「아니오 (단일호기)」 창이고, 계획이 confirm 을 빼 115초 동안 창만 떠
  // 있었다(2026-10-14). 호기를 골랐으면 그 호기만, 아니면 현장 전부 — 스킬이 말하는 것과 같다.
  if (button.replace(/\s+/g, '') === '정보센터연동') return oneRow ? '아니오 (단일호기)' : '예 (일괄연동)';
  return undefined;
}

/**
 * **고른 문서 안의 표에서 고를 한 줄**(`then`). 보수현장은 현장을 고르면 호기 표(`units`)가 뜨고, 승강기번호
 * 넣기·정보센터연동은 그 표에서 고른 호기에 일한다. 값이 빈 것은 버린다.
 */
function innerRow(value: unknown): { stateKey: string; match: Record<string, string> } | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const stateKey = String(raw.stateKey ?? '').trim();
  const match = raw.match && typeof raw.match === 'object' && !Array.isArray(raw.match)
    ? Object.fromEntries(Object.entries(raw.match as Record<string, unknown>)
      .map(([key, one]) => [key, String(one ?? '').trim()]).filter(([, one]) => one))
    : {};
  return stateKey && Object.keys(match).length ? { stateKey, match } : undefined;
}

/** 질문에 쓰인 말에서 화면 이름 쪽 낱말을 넓힌다. */
export function expanded(question: string): string[] {
  const words: string[] = [];
  for (const [said, means] of Object.entries(SYNONYMS)) {
    if (question.includes(said)) words.push(...means);
  }
  return words;
}

/**
 * 공백을 지운 좌표에서 **낱말이 시작하는 자리**들.
 *
 * 사람은 `점검 계획` 이라 띄어 쓰고 화면은 `점검계획등록` 이라 붙여 쓴다 — 그래서
 * 질문의 공백을 지우고 화면 이름을 찾는다. 그런데 그러면 **낱말 가운데에도** 박힌다:
 * 「연차 하루 신청서 … 사유는 **개인 사정**이야」의 `개인사정` 안에 `인사` 가 들어 있어
 * 휴가 신청에 **인사등록이 1등**이 됐다(실측 2026-09-14). 「안전교육 일지」도 `교육보고서`
 * 를 끌어올린다. 낱말 첫 글자에서 시작할 때만 센다 — 벤치 50건에서 잃는 것 0, 없애는 것 2.
 */
function wordStarts(question: string): Set<number> {
  const starts = new Set<number>();
  let index = 0;
  let atStart = true;
  for (const letter of question) {
    if (/\s/.test(letter)) { atStart = true; continue; }
    if (atStart) starts.add(index);
    atStart = false;
    index += 1;
  }
  return starts;
}

/** 핵심 구절이 질문의 **낱말 첫머리**에 나오는가. 가운데에 박힌 것은 우연이다. */
function phraseAtWordStart(compact: string, topic: string, starts: Set<number>): boolean {
  if (topic.length < 2) return false;
  for (let at = compact.indexOf(topic); at >= 0; at = compact.indexOf(topic, at + 1)) {
    if (starts.has(at)) return true;
  }
  return false;
}

/** 화면 이름의 핵심 구절. `점검계획등록` → `점검계획`, `장기미수현황 (수금관리)` → `장기미수`. */
export function screenTopic(name: string): string {
  return name
    .replace(/\([^)]*\)/g, '')
    .replace(/\s+/g, '')
    .replace(/(?:현황|등록|조회|관리|보고서|화면|\.)+$/g, '');
}

/** 사람이 그 화면을 **이름으로** 말했나. 후보 고르기와 같은 잣대(낱말 첫머리)를 쓴다. */
export function namesScreen(question: string, name: string | undefined, besides: string[] = []): boolean {
  if (!name) return false;
  let compact = question.replace(/\s+/g, '');
  // **다른 화면 이름이 차지한 자리는 빼고 본다.** 「유지관리 매출 생성 현황」의 `매출` 은
  // 유지관리매출생성의 일부로 나온 것이지 매출현황을 말한 것이 아니다 — 그것을 「둘 다
  // 말했다」로 세어 스킬이 맞는 화면을 끌어갔다(2026-09-23 실측).
  for (const other of besides) {
    const topic = screenTopic(other);
    if (topic.length < 2) continue;
    compact = compact.split(topic).join('#'.repeat(topic.length));
  }
  return phraseAtWordStart(compact, screenTopic(name), wordStarts(question));
}

/**
 * **사람이 화면 이름을 통째로 말했으면 그 화면이 이긴다.** 그 화면을 안 가리키는 스킬은 내려놓는다.
 *
 * 「이번 달 공사 결과 보고서 보여줘」에 공사주간실적 스킬이 뽑혀 공사현황을 고정했고, 모델은
 * 공사결과보고서를 고를 기회도 없었다(실제 테넌트, 2026-09-25). `spokeOtherScreen` 은 모델이 고른
 * 화면을 사람이 말했을 때만 비켜 가므로 여기 안 걸린다.
 *
 * **이름 전체**가 들어 있을 때만 — 띄어쓰기는 무시하고 네 글자 이상. `매출` 처럼 짧은 말은
 * 여러 화면에 걸리므로 셈하지 않는다. 겹치면 가장 긴 이름이다.
 */
export function namedScreen(question: string, screens: AgentScreen[]): AgentScreen | undefined {
  const compact = question.replace(/\s+/g, '');
  return screens
    .filter((screen) => {
      const name = (screen.name ?? '').replace(/\s+/g, '');
      return name.length >= 4 && compact.includes(name);
    })
    .sort((left, right) => right.name.length - left.name.length)[0];
}

export function skillsBesideNamedScreen<T extends { kind?: string; preferredRoutes?: string[] }>(
  question: string,
  skills: T[],
  screens: AgentScreen[],
): T[] {
  const named = namedScreen(question, screens);
  if (!named) return skills;
  return skills.filter((skill) => skill.kind === 'artifact'
    || !skill.preferredRoutes?.length
    || skill.preferredRoutes.includes(named.route));
}

export function candidates(question: string, screens: AgentScreen[]): AgentScreen[] {
  const pool = screens.filter(usable);
  const compactQuestion = question.replace(/\s+/g, '');
  const wordHeads = wordStarts(question);
  const letters = new Set(compactQuestion);
  const synonyms = expanded(question);
  const scored = pool.map((screen) => {
    // 화면 이름만 보면 "회사 연차 평균"도 개인 휴가신청서로 간다. 검색 서버가
    // 없을 때도 표 컬럼과 검색조건의 뜻을 후보 선정에 쓴다.
    const meaning = [
      screen.name,
      ...screen.fields.map((field) => field.label ?? field.key),
      ...screen.grids.flatMap((grid) => grid.columns.flatMap((column) => [column.id, column.text])),
      ...(screen.scalars ?? []).flatMap((scalar) => [scalar.key, scalar.label]),
      ...(screen.draftFields ?? []).flatMap((field) => [field.key, field.label ?? '']),
    ].join('');
    const semantic = Array.from(letters).filter((letter) => meaning.includes(letter)).length
      / Math.max(1, letters.size);
    // 사람이 쓴 말과 화면 이름이 다를 때를 잇는다. `수금` 은 `입금현황` 을 뜻한다.
    const synonym = synonyms.some((word) => screen.name.includes(word)) ? 2 : 0;
    const nameOverlap = Array.from(screen.name).filter((letter) => letters.has(letter)).length
      / Math.max(1, screen.name.length);
    const topic = screenTopic(screen.name);
    // 글자 집합 점수는 짧은 일반 화면(인사등록 등)을 과대평가한다. 화면의 핵심
    // 구절이 질문에 그대로 있으면 훨씬 강한 신호로 본다.
    const phrase = phraseAtWordStart(compactQuestion, topic, wordHeads) ? 3 : 0;
    return { screen, score: phrase + synonym + semantic + nameOverlap * 0.25 };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, SHORTLIST).map((entry) => entry.screen);
}

/**
 * 검색이 준 경로 순서대로 화면을 세운다.
 *
 * 표가 없는 화면은 조회해도 읽을 것이 없으므로 뺀다. 검색이 아무것도 못
 * 찾으면 이름 겹침으로 떨어진다 — 후보를 비우면 고를 기회가 없어진다.
 */
/**
 * `trusted` 는 **스킬이 이름을 대고 지목한 화면**이다. 그때는 쓸모 판정을 건너뛴다.
 *
 * `usable` 은 열이 둘 이상인 표가 있거나 신규+초안칸이 있어야 쓸모 있다고 본다.
 * 달력 화면(스케쥴관리)은 표에 열이 하나도 없고 카탈로그상 동작도 조회·인쇄뿐이라
 * **후보에 아예 오르지 않았다** — 정작 날짜를 누르면 그날치 창이 열려 쓸 수 있는데도.
 * 스킬이 그 화면을 어떻게 쓰는지 적어 두었다면 그것이 더 정확한 앎이다.
 */
async function ranked(
  routes: string[],
  screens: AgentScreen[],
  trusted = false,
): Promise<AgentScreen[]> {
  const byRoute = new Map(screens.map((screen) => [screen.route, screen]));
  const picked: AgentScreen[] = [];
  for (const route of routes) {
    const screen = byRoute.get(route);
    if (screen && (trusted || usable(screen)) && !picked.includes(screen)) picked.push(screen);
    if (picked.length >= SHORTLIST) break;
  }
  return picked;
}

/**
 * 그 화면에서 코드로 고르는 칸의 **고를 수 있는 값**.
 *
 * 없는 값을 지어내는 병은 프롬프트를 줄여서 못 고친다 — 본 적 없는 닫힌 어휘는
 * 지어낼 수밖에 없다. 그래서 이것만은 실어 보낸다. 대신 **그 턴이 실제로 쓰는 화면의
 * 것만** 싣는다(makePlan 의 writeRoutes).
 *
 * 열거할 수 없는 목록(거래처 11,270개)과 못 받은 목록은 싣지 않는다. 앞의 것은 실으면
 * 그 자체가 사고이고, 뒤의 것은 실을 것이 없다.
 */
export function codeChoices(screen: AgentScreen, lists: CodeLists | undefined): string {
  if (!lists) return '';
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const field of [...(screen.draftFields ?? []), ...screen.fields] as AgentField[]) {
    if (seen.has(field.key)) continue;
    const list = codeListFor(field, lists);
    // 팝업으로 고르는 칸은 받아 둔 목록에서, 콤보로 고르는 칸은 화면 정의에서 나온다.
    // 사람에게는 둘 다 "고를 수 있는 값" 하나다 — 교육분류가 콤보라 앞엣것만 실으면
    // 자체교육·외부교육 둘뿐인 칸에 교육 이름을 계속 지어낸다.
    // **코드와 이름을 공백으로 잇지 않는다.** `13 사원` 으로 보였더니 계획이 그 글자를
    // 통째로 값에 넣었고 화면이 거절했다(실측 2026-09-01). 프롬프트가 그것을 가르친
    // 셈이다. `=` 로 이어 두 쪽이 다른 것임을 눈에 띄게 한다.
    const choices = list?.items?.length
      ? list.items.map((item) => `${item.code}=${item.name}`)
      : field.options ?? [];
    if (!choices.length) continue;
    seen.add(field.key);
    lines.push(`    ${field.key}(${field.label ?? list?.label ?? field.key}): ${choices.join(', ')}`);
  }
  return lines.length
    ? `\n  코드 값 — 이 목록 밖은 쓸 수 없다. \`13=사원\` 은 코드와 이름이다 — **한쪽만** 값으로 쓰고 통째로 쓰지 마라:\n${lines.join('\n')}`
    : '';
}

/** 화면 설명을 프롬프트에 실을 길이. 지금 자료의 첫 문장은 최장 123자다. */
const DESCRIPTION_LIMIT = 160;

/**
 * 설명을 **첫 문장만** 남기고, 그마저 길면 자른다.
 *
 * 형제를 가르는 정보는 전부 첫 문장에 있다 — 「점검**계획**을 조회하고」 ↔ 「점검
 * **결과**를 기간·사용자·현장·결과별로 조회하고」. 뒤 문장은 오류 대처 같은 각론이라
 * 화면을 고르는 데 안 쓰인다. 그런데 화면 열둘이 한 프롬프트에 실리므로 통째로
 * 실으면 그 각론이 열두 배로 늘어 정작 고를 것을 묻는다.
 */
function firstSentence(description: string): string {
  const text = description.trim();
  // 문장부호 뒤가 공백이거나 끝일 때만 문장 끝으로 본다. 소수점·약어에서 안 끊긴다.
  const end = text.search(/[.!?](?:\s|$)/);
  const sentence = end >= 0 ? text.slice(0, end + 1) : text;
  return sentence.length > DESCRIPTION_LIMIT
    ? `${sentence.slice(0, DESCRIPTION_LIMIT - 1).trimEnd()}…`
    : sentence;
}

/**
 * **펼쳐 읽을 수 있는 상세 표에 그 길을 적는다.** 「일자 칸이 없다」며 되물었던 표다 — 위 표 행마다 읽으면
 * 위 표 칸(일자)이 행마다 붙는다(2026-10-19). 문서 목록 화면에도 쓰므로 「날마다」라고 하지 않는다.
 */
/** 드라이버가 펼쳐 읽기 한 번에 쓰는 시간(`SWEEP_MS`)과 같다. */
const SWEEP_BUDGET_MS = 120_000;

/**
 * **한 번에 펼쳐 읽을 위 표 줄 수.** 시한의 80% 안에 끝나는 만큼, 많아야 62줄(한 달 근무일 두 번).
 * 줄마다 8초 걸리는 화면(매출등록·CMS)에 62줄을 내면 시한을 네 배 넘겨 앞 몇 줄만 읽힌다(검토, 2026-10-21).
 */
export function sweepLimit(msPerRow?: number): number {
  if (!msPerRow) return 62;
  return Math.max(1, Math.min(62, Math.floor((SWEEP_BUDGET_MS * 0.8) / msPerRow)));
}

function sweepNote(master: AgentScreen['grids'][number]['master'], detail: string): string {
  if (!master) return '';
  const range = master.rangeKey
    ? `, 범위는 sweep:{from,to} — 위 표 「${master.rangeLabel ?? master.rangeKey}」(${master.rangeKey}) 값`
    : '';
  const pace = master.msPerRow ? ` 한 줄에 약 ${Math.max(1, Math.round(master.msPerRow / 1000))}초 —` : '';
  return ` — 위 표 여러 줄의 상세가 필요하면 sweep 으로 펼쳐 읽어라(위 표 칸이 행마다 붙는다${range}).`
    // 위 표에 거는 계획이 거듭 나왔다 — 오류로 고쳐 주면 계획을 한 번 더 부른다(인사등록, 2026-10-21).
    + ` sweep 은 이 표(${detail})에 건다 — 위 표 ${master.stateKey} 에 걸지 마라.`
    + (master.joinHint ? ` ${master.joinHint}.` : '')
    + ' 앞선 조회가 뽑은 대상만 읽으려면 sweep:{matchFrom:{label:앞 조회 이름, column:앞 조회의 칸, key:위 표의 같은 값 칸}}'
    + `${pace} 위 표가 ${sweepLimit(master.msPerRow)}줄을 넘으면 조건을 좁혀라`;
}

export function describe(screen: AgentScreen, lists?: CodeLists): string {
  const fields = screen.fields
    .map((field) => `${field.key}:${field.widget}(${field.label ?? field.key}${
      field.example ? `, 예:${field.example}` : ''
    }${field.options?.length ? `, 선택:${field.options.join('|')}` : ''
    })`)
    .join(', ') || '없음';
  const byKey = new Map<string, { id: string; text: string }[]>();
  // **달력인지 표인지 프롬프트에 적는다.** 이 사실은 이미 갖고 있는데 안 보여 줬다.
  // 「이번 달 당직 계획 보여줘」에 모델은 `data[]`(칸 0개인 달력)와 `data[…47칸]`(당직현황)
  // 을 나란히 보고 **뒤를 골랐다** — 화면에 자료가 있어 보이는 쪽이다. 점검계획도 같은
  // 모양으로 점검결과등록에 졌다(실측 2026-09-14). 계획 화면은 달력이라 칸이 없는 것이지
  // 읽을 것이 없는 것이 아니다. 문구로 구슬리는 것이 아니라 있는 사실을 주는 일이다.
  const kinds = new Map<string, string>();
  const details = new Set<string>();
  const masters = new Map<string, NonNullable<AgentScreen['grids'][number]['master']>>();
  for (const grid of screen.grids) {
    if (grid.detail) details.add(grid.key);
    if (grid.master) masters.set(grid.key, grid.master);
    const columns = byKey.get(grid.key) ?? [];
    for (const column of grid.columns) {
      if (!columns.some((candidate) => candidate.id === column.id)) columns.push(column);
    }
    byKey.set(grid.key, columns);
    if (grid.kind) kinds.set(grid.key, grid.kind);
  }
  const shape = (key: string, columns: { id: string; text: string }[]): string => {
    if (kinds.get(key) === 'calendar') return '달력(날짜별로 그린다)';
    // 표 없는 입력 양식. 한 건이라 행 수를 세면 늘 1이다 — 그 칸의 값을 읽는 화면이다.
    if (kinds.get(key) === 'record') {
      return `한 건짜리 양식: ${columns.map((column) => `${column.id}(${column.text})`).join(', ')}`;
    }
    // 칸 이름을 못 뽑은 표다. 「빈 표」로 보이면 자료가 있는 화면을 안 고른다.
    if (kinds.get(key) === 'observed' && !columns.length) return '칸 이름 모름';
    return columns.map((column) => `${column.id}(${column.text})`).join(', ');
  };
  const grids = Array.from(byKey, ([key, columns]) => ({ key, columns }))
    .filter(({ key }) => !isSelectionGrid(key))
    // **선택된 행의 상세 표라고 적는다.** 모르면 모델은 그 표를 목록으로 고르고, 답은
    // 한 그룹의 행 수를 전체로 센다(비용항목등록 「총 8건」, 2026-09-23).
    .map(({ key, columns }) => `${key}[${shape(key, columns)}]${
      details.has(key) ? '(위 목록에서 고른 한 행의 상세 — 목록 전체가 아니다)' : ''
    }${sweepNote(masters.get(key), key)}`).join(' / ');
  const selections = Array.from(byKey, ([key, columns]) => ({ key, columns }))
    .filter(({ key }) => isSelectionGrid(key))
    .map(({ key, columns }) => `${key}[${
      columns.map((column) => column.text).join(', ')
    }]`).join(' / ');
  // 스칼라가 없는 화면은 이 줄 자체가 없다 — 프롬프트가 예전과 같아야 한다.
  const scalars = screen.scalars?.length
    ? `\n  화면 값: ${screen.scalars.map((s) => `${s.key}(${s.label})`).join(', ')}`
    : '';
  const drafts = screen.draftFields?.length
    ? `\n  신규 입력: ${screen.draftFields.map((field) => `${field.key}:${field.widget}(${field.label ?? field.key})`).join(', ')}`
    : '';
  const actions = screen.actions?.length ? `\n  작업: ${screen.actions.join(', ')}` : '';
  // 화면 고유 버튼. 문서를 다음 문서로 잇는 길이 여기 있다 — 견적서등록의 `자재청구서`
  // 가 목록에 없으면 계획은 그런 길이 있다는 것조차 모르고 다음 화면에서 신규를 누른다.
  const buttons = screen.buttons?.length
    ? `\n  화면 버튼: ${screen.buttons.map((one) => one.label).join(', ')}`
    : '';
  // **표 안에도 버튼이 있다.** 행마다 하나씩 달린 아이콘이고, 그 행의 문서를 연다 —
  // 점검결과등록의 `점검일지` 가 그것이다. 안 알려 주면 계획은 그런 길이 있다는 것조차
  // 모른다. 어느 행인지 골라야 하므로 **표와 칸 이름을 같이** 적는다.
  const rowButtons = screen.grids
    .flatMap((grid) => (grid.rowButtons ?? []).map((one) => `${grid.key}.${one.column}(${one.label})`));
  const inRow = rowButtons.length
    ? `\n  표 안 버튼(그 행을 값으로 짚어 누른다): ${Array.from(new Set(rowButtons)).join(', ')}`
    : '';
  // 탭이 있으면 **저장이 탭마다 갈린다.** 그 사실을 안 알려 주면 계획은 문서 하나만 내고,
  // 가족·경력은 영영 안 채워진다. 문의의 43.8%가 탭 화면이다.
  //
  // **지금 화면에 그 표가 안 보인다고 없는 것이 아니다.** 탭 표는 그 탭을 열 때 비로소
  // 상태에 나타난다. 그 말을 안 해 주면 계획이 거절한다 — 실제로 「확인되는 표는
  // staffList뿐이라 가족사항 입력 행을 지정할 수 없습니다」로 끝난 판이 있다.
  // **머리와 함께 저장되는 탭은 따로 적는다.** 매입등록·매출등록은 상세 탭을 머리와 한 번에
  // 저장한다. 그것까지 걸음으로 나누자 머리만 먼저 저장하다 `매입할 비용이 없습니다` 로
  // 막혔다(2026-09-25). 그런 탭은 prepare 의 rows 에 tab 을 주어 한 번에 낸다.
  const tabLabel = (tab: NonNullable<AgentScreen['tabs']>[number]): string => (
    tab.columns?.length
      ? `${tab.title}→${tab.stateKey}[${tab.columns.map((key) => (
        tab.labels?.[key] ? `${key}(${tab.labels[key]})` : key
      )).join(', ')}]`
      : `${tab.title}→${tab.stateKey}`
  );
  const apart = (screen.tabs ?? []).filter((tab) => !tab.withHead);
  const together = (screen.tabs ?? []).filter((tab) => tab.withHead);
  const tabs = (apart.length
    ? `
  탭(저장이 탭마다 갈린다. 표는 그 탭을 열 때 나타나므로 지금 안 보여도 걸음으로 낸다): ${apart.map(tabLabel).join(', ')}`
    : '') + (together.length
    ? `
  머리와 함께 저장되는 탭(걸음으로 나누지 않는다 — prepare 의 rows 에 stateKey 와 tab 을 주어 한 번에 낸다): ${together.map(tabLabel).join(', ')}`
    : '');
  const choices = codeChoices(screen, lists);
  // 설명은 **이름 바로 옆**이다. 형제를 가르는 단서라 뒤에 두면 긴 칸 목록에 묻힌다.
  // 설명이 없는 화면은 이 자리가 통째로 비어 줄이 예전과 같다.
  const purpose = screen.description?.trim() ? ` — ${firstSentence(screen.description)}` : '';
  return `${screen.route} ${screen.name}${purpose}\n  검색조건: ${fields}\n  표: ${grids || '없음'}${
    selections ? `\n  필수 선택목록(자동 전체선택): ${selections}` : ''
  }${scalars}${drafts}${actions}${buttons}${inRow}${tabs}${choices}`;
}


/**
 * **여기까지가 매번 같은 지침이다** — 게이트웨이가 이 표시 앞을 developer 메시지로, 뒤를 user 메시지로 나눠 보내
 * 모델 쪽 캐시가 앞부분을 다시 쓰게 한다(질문을 한 메시지에 이어 붙이면 캐시 적중 0%, 2026-10-15 게이트웨이 로그).
 * 보이지 않는 글자 둘이라 옛 게이트웨이를 거쳐도 모델이 읽는 말은 그대로다. 과제 프롬프트도 쓴다.
 */
export const CACHE_SPLIT = String.fromCharCode(0x2063, 0x2063);

export const PLAN_PROMPT = `너는 엘리베이터 회사 ERP 를 대신 조회해 주는 도우미다.
질문에 답하려면 어느 화면을 어떤 조건으로 조회해야 하는지 정한다.

**너는 조회 계획만 세운다. 계산하지 않고 답도 쓰지 않는다.**

규칙:
- 아래 목록에 있는 화면만 쓴다
- 검색조건은 그 화면에 있는 key 만 쓴다
- 표는 그 화면에 표시된 key를 **철자까지 그대로** 쓴다. 흔히 쓰는 data로 바꾸지 마라
- openChk, openChk2 같은 "필수 선택목록"은 조회 결과 표가 아니며 rowsStateKey로 고르지 않는다. 실행기가 조회 전에 자동으로 전체 선택한다
- 평균·합계·그룹별 질문은 **같은 표**에 대상 컬럼과 계산할 수치 컬럼이 모두 있어야 한다
- "회사 평균"처럼 여러 사람을 묻는 질문은 사원 컬럼과 사람별 값이 있는 표를 고른다.
  현재 사용자 한 명의 scalars를 회사 전체 값처럼 쓰지 마라
- "지점별·부서별·사업장별" 질문은 해당 조직 컬럼과 금액 컬럼이 같은 표에 있는 화면을 고른다
- 별도 회사 용어 정의가 없으면 사용자가 말한 "지점"은 "사업장"으로 해석한다. 회사구분·부서·현장은 사업장과 다른 분류다
- 사업장명과 월별 매출금액이 같은 표에 있으면 그 화면을 직접 고른다. 사업장 컬럼이 없는 행별 매출현황을 먼저 고르고 되묻지 마라
- 비교하는 질문이면 조건만 다른 조회를 여러 번 넣는다. label 은 서로 달라야 한다
- 먼저 질문을 데이터 계약으로 바꾼다. dimensions(분류축), measures(값/계산식), operations(필터·결합·집계·정렬), artifact를 적는다. artifact는 none/xlsx/csv/docx/pdf 중 하나다
- 기준일 전후를 비교하는 질문(「10/15 알림 뒤로 늘었나」)이면 operations 에 compare-periods 를, dimensions 에 '기준일:YYYYMMDD' 를 적는다. 두 기간을 다 덮게 조회한다
- 각 조회의 requiredColumns에는 계산에 실제로 쓸 **컬럼 id**를 모두 적는다. 선택한 표에 없는 컬럼을 적을 수 없다
- 이전 대화의 기간·대상은 이어받을 수 있지만 화면 경로는 이어받지 않는다. 새 질문의 requiredColumns를 모두 제공하는 화면인지 다시 확인한다
- 순위·정렬·그룹합계·여러 달 결합은 화면이 해 주리라 기대하지 않는다. 필요한 원천 행을 조회하고 뒤 계산 단계가 처리하게 한다
- 데이터 원천이 제공하지 않는 기준을 선택지로 제안하지 않는다. 금액만 있으면 금액으로 조회하고, 금액과 건수가 모두 가능하며 답의 의미가 달라질 때만 기준을 묻는다
- 회사 용어가 스킬에서 미확정 또는 모호하다고 명시되면 지표 기준보다 업무 분류를 먼저 한 번만 확인한다. 모르는 용어를 비슷한 ERP 항목으로 자동 치환하지 않는다
- 수익/이익은 매출·수금과 같은 말이 아니다. 수익 컬럼이 있거나 공사금액과 매입단가처럼 계산 근거가 같은 표에 있는 화면을 고른다
- 검색조건에 '예:'가 있으면 그 예시와 같은 자릿수·형식으로 쓴다. 예: 202601이면 202501
- 예시가 없는 DateTextBox 년도는 "2026", 날짜는 "2026-08-01"처럼 쓴다
- "이번 달" 날짜 범위는 오늘까지만이 아니라 그 달 1일부터 말일까지다. 2026-08이면 2026-08-01~2026-08-31이다
- 이 대화는 회사 ERP 안이다. 낯선 말은 일반 국어(문법·맞춤법)로 읽지 말고 ERP 업무 말의 오타·준말로 먼저 읽는다
  (시제→시재, 결제→결재). 그래도 모르겠어서 되묻는다면 아래 화면 가운데 그 말에 가까운 업무를 보기로 내라
- "요 근래"·"요즘"·"최근"처럼 기간 없이 말하면 오늘까지 최근 3개월이다. 오늘이 2026-10-22면 2026-07-23~2026-10-22다.
  달로 나누는 화면(발생월 등)이면 이번 달까지 세 달(2026-08~2026-10)로, 그 화면의 달 칸으로 센다 — 화면에서 사람이 보는 숫자와 같게
- "지난달 것까지"·"지난달도"처럼 기간 말이 함께 있으면 그 말을 따른다. 「요새 점검 문제없어? 지난달 것까지」는 이번 달과 지난달
  두 달이다(세 달로 늘리지 않는다)
- 사용자가 "매출 현황"처럼 일반적인 업무명을 말해도 화면 이름만 따라가지 말고, requiredColumns를 한 표에서 충족하는 화면을 고른다
- DateTextBox의 key가 yearmon/stmon/endmon처럼 mon으로 끝나거나 이름이 월이면 YYYYMM으로 쓴다
- 1년을 조회해야 하는데 화면에 월 조건 하나만 있으면 같은 화면을 12번, 서로 다른 label과 YYYYMM 조건으로 조회한다
- 사용자가 90일을 골랐고 화면 조건이 미수기간(월)이면 month3은 "3"으로 변환한다. 180일은 "6"이다
- 사용자가 장기 미수 판단을 "기간 기준"으로 이미 골랐으면 금액 기준을 다시 묻지 않는다. 해당 month3으로 거르고 미수잔액 내림차순으로 심각도를 정한다
- "A 중 B만"처럼 교집합을 묻는데 한 표에 모든 조건과 결과가 없으면 여러 화면을 조회한다. 두 표의 같은 식별자(현장코드 등)를 requiredColumns에 넣고 뒤 계산 단계에서 join하게 한다
- 사업장·부서·구분처럼 **전체 선택지가 있는 조건은 값마다 조회를 만들지 마라.** "전체"로 한 번 조회하고, 나누는 일은 결과 열을 보고 계산 단계가 한다. 사업장 열둘을 각각 조회한 계획이 조회 예산을 다 태우고 답을 못 낸 적이 있다. 기간은 예외다 — 화면에 월 조건 하나뿐이면 위 규칙대로 월마다 조회한다
- ComboBox는 화면에 보이는 글자를 쓴다
- CheckBox는 체크가 필요하면 "1", 해제하려면 "0"을 쓴다. 라벨을 값으로 쓰지 마라

- 발화에 [민감값01] 같은 자리표가 있으면 **그 글자를 그대로 옮겨 적는다.** 풀어 쓰려 하지
  말고, 없는 값이라고도 하지 마라 — 실행기가 화면에 넣기 직전에 진짜 값으로 되돌린다.
  주민번호처럼 가려 둔 값이 그렇게 온다

JSON 만 출력한다. 다른 말을 붙이지 마라. 셋 중 하나를 고른다.

1. 조회해서 답할 수 있으면
{"reason":"왜 이 화면인지 한 줄","requirements":{"dimensions":["현장명"],"measures":["수익=공사금액-매입단가"],"operations":["현장별 합계","수익 내림차순"],"artifact":"xlsx"},"retrievals":[{"label":"2025-01","route":"/w_...","rowsStateKey":"data","conditions":{"yearmon":"202501"},"requiredColumns":["projnm","compamt","phm_mamt"]}]}

2. "신청해줘·등록해줘·작성해줘"처럼 새 문서 작성을 요청하고 화면에 new 작업과 신규 입력이
있으면 신규 초안을 만든다. 질문에서 확실히 알 수 있는 입력만 넣고 모르는 값은 지어내지 마라.
저장·삭제는 계획하지 않는다(저장은 런타임이 한다). **사람이 결재를 올리라고 말했으면**
("올려줘"·"상신해줘"·"결재 올려") submit 을 true 로 더한다 — 저장한 뒤 결재상신까지 한다.
말하지 않았으면 넣지 마라
{"kind":"prepare","route":"/w_...","fields":{"vafrdate":"2026-08-10"},"reuse":{"rowsStateKey":"holidayList","match":{"daynum":"1.0"},"fields":["gowhere","remark"]},"reason":"..."}
견적서·청구서처럼 **품목이 있어야 쓸모 있는 문서**는 상세행도 같이 낸다. 금액은 넣지
마라 — 품목과 수량을 넣으면 화면이 계산한다. 단가는 사람이 말했을 때만 uamt 에 넣는다. 품목이 없으면 그 문서는 다음 단계로 이어지지
않는다("조회된 자료가 없습니다" 로 막힌다)
{"kind":"prepare","route":"/w_tb_e451","fields":{"constnm":"로프 교체"},"rows":{"stateKey":"dataMaterials","items":[{"pname":"WIRE ROPE","qty":"1"}]},"reason":"..."}
상세행의 stateKey 는 **그 화면의 표 목록에 있는 이름**이어야 한다. 지어내면 행이 하나도
늘지 않고 초안이 통째로 실패한다. 품목처럼 목록에서 고르는 칸은 **코드 칸에 이름을**
넣는다(pcode, actcd 처럼 cd·id·code 로 끝나는 칸) — 화면이 팝업을 열어 골라 주고
품명·단가가 따라온다. 이름 칸에만 넣으면 코드가 비어 저장이 막힌다

2-1. **이미 있는 문서를 고치는** 요청이면(고장 접수를 완료 처리, 결과를 적어 넣기)
새로 만들지 말고 그 행을 골라 고친다. 새로 만들면 같은 문서가 하나 더 생길 뿐이다
{"kind":"prepare","route":"/w_tb_e411_01","edit":{"rowsStateKey":"breakCompleteList","match":{"recedate":"20260813","recenum":"001"}},"fields":{"compdate":"20260813","resultcd":"처리완료"},"reason":"..."}
**고치는 것과 더하는 것을 가른다.** 이미 있는 줄의 값을 바꾸라는 말이면
("3만원이었는데 4만원이야", "수량 두 개로 고쳐줘") rows 로 내지 마라 — 줄이 하나 더 붙어
두 배가 된다. 그 줄의 칸을 cells 로 바꾼다. 문서를 고르는 표와 칸을 고칠 표가 다르면
cellsStateKey·cellsMatch 로 짚는다
{"kind":"prepare","route":"/w_tb_pb401","edit":{"rowsStateKey":"tripList","match":{"outdate":"20260824","title":"광주 출장"},"cellsStateKey":"tripDetailList","cellsMatch":{"productnm":"교통비"},"cells":{"uamt":"40000"}},"fields":{},"reason":"..."}
줄을 **더하는** 말이면("숙박비도 8만원 넣어줘") 그때가 rows 다

2-2. 화면에 **「탭(저장이 탭마다 갈린다)」 목록이 적혀 있으면** 저장이 탭마다 갈린다. 머리 문서를 먼저 저장해야
번호가 생기고 그 뒤에야 나머지 탭이 붙는다. 그럴 때는 걸음으로 나눠 낸다. 걸음 하나는
위 2 의 초안 하나와 같다
{"kind":"sequence","route":"/w_p2110","steps":[{"label":"기본정보","fields":{"pernm":"홍길동","entdate":"20260901"},"save":true},{"label":"가족","tab":"가족","rows":{"stateKey":"families","items":[{"faminm":"홍순이"}],"addLabel":"신규"},"save":true}],"reason":"..."}
첫 걸음이 머리 문서다. tab 은 화면에 적힌 탭 이름 그대로 쓴다. rows.stateKey 도
탭 목록에 적힌 이름을 쓴다 — 지어내면 그 걸음이 통째로 실패한다. 행을 더하는 단추가
"추가" 가 아닌 화면이 있다(인사등록은 화면의 "신규" 가 지금 탭에 한 줄을 붙인다).
그럴 때만 addLabel 을 준다. **사용자가 말하지 않은 탭은 걸음으로 만들지 마라**
「머리와 함께 저장되는 탭」은 걸음이 아니다. 머리 칸과 그 탭의 줄을 prepare 하나로 낸다
{"kind":"prepare","route":"/w_tb_ca640","fields":{"cltcd":"OO상사","remark":"부품 매입"},"rows":{"stateKey":"buys","tab":"매입상세","items":[{"remark":"부품","samt":"500000"}]},"reason":"..."}

2-3. 사람이 **파일을 올렸고** 그것을 지금 보고 있는 문서에 붙여 달라는 요청이면
("이 파일 첨부해줘", "방금 만든 문서에 이거 붙여줘")
{"kind":"attach","route":"/w_p2110","reason":"..."}
문서 번호도 파일도 **화면이 안다.** 네가 낼 것은 어느 화면인가뿐이다.
값을 채워 넣는 일이면 이 갈래가 아니다 — 그것은 prepare 나 sequence 다

3. 앞에서 만든 문서를 **다음 문서로 잇는** 요청이면("그 견적서로 자재청구서 올려줘")
화면에 적힌 버튼 이름을 그대로 쓴다. 새 화면에서 신규를 누르는 것이 아니라, 문서를 만든
그 화면의 버튼을 누르는 길이다
{"kind":"operate","route":"/w_tb_e451","button":"자재청구서","fields":{"modalOrderMaterialsRemark":"로프 교체 자재 청구"},"selectKey":"dataModalOrderMaterials","reason":"..."}
그 창이 값을 요구하면 fields 로 같이 내고, 창 안 표에서 골라야 하면 selectKey 를 준다 —
둘 다 비면 화면이 "비고를 입력하세요"·"선택을 하나이상 하십시요" 로 되돌려보낸다
**버튼이 표 안에 있으면 어느 행인지도 같이 내라.** 화면 설명의 \`표 안 버튼\` 줄에 적힌
것이 그것이고, 행마다 하나씩 달린 아이콘이라 이름만으로는 못 누른다. row 로 표·칸·
그 행을 가릴 값을 준다
{"kind":"operate","route":"/w_tb_e035_03","button":"점검일지","row":{"stateKey":"data","column":"self","match":{"actnm":"덕봉빌딩"},"conditions":{"planmon":"202606"}},"reason":"..."}
**목록 전체에 대고 누르는 버튼이면 list 로 그 목록을 먼저 조회하게 하라.** 화면을 열면 목록은
기본 달이거나 비어 있다. 고른 행에만 일하는 버튼(전자지로생성)은 check 로 행을 고르게 한다
{"kind":"operate","route":"/w_tb_e050w_02","button":"전자지로생성","confirm":"예","list":{"stateKey":"data","conditions":{"stmon":"202609"},"check":true},"reason":"..."}
**문서 하나에 대고 누르는 단추면 focus 로 그 문서를 고르게 하라**(견적서복사처럼 목록에서 고른 문서에 일하는 단추).
match 는 목록 열 값, conditions 는 그 문서가 보이게 할 조회 조건 — 기간을 말하지 않았으면 1년 전~오늘로 넓힌다
{"kind":"operate","route":"/w_tb_e451","button":"견적서복사","focus":{"stateKey":"data","match":{"actnm":"덕봉빌딩"},"conditions":{"stdate":"20251001","enddate":"20261013","searchQuery":"덕봉빌딩"}},"reason":"..."}
문서를 고른 뒤 그 안의 표에서 한 줄을 더 골라야 하면(보수현장 → 호기) focus.then 에 그 표와 줄을 준다 —
prepare 의 edit.then 도 같다. 스킬이 말할 때만 쓴다
**conditions 를 반드시 같이 내라.** 화면을 열면 표는 비어 있거나 다른 달이 담겨 있어서,
조건이 없으면 고를 행이 하나도 없다. 조회 화면에서 쓰는 그 조건 그대로다
match 는 사람이 말한 이름을 그대로 쓰면 된다 — 화면에 "김영자빌딩(대성빌딩)" 으로
들어 있어도 앞부분이 겹치면 같은 곳으로 본다. 다만 **여럿이 걸리면 누르지 않고 되묻는다**
**보내는 것은 여기가 아니다.** \`문자전송\`·\`전송\`·\`메일\`·\`카톡\` 같은 이름의 버튼을
operate 로 누르지 마라 — 그것은 아래 4번의 send 다. 화면 목록에 그런 버튼이 적혀 있어도
지금 열린 화면에는 없을 수 있다("문자전송 버튼이 화면에 없다" 로 끝났다)

4. 메일·문자·팩스·카카오톡으로 **보내 달라**는 요청이면 보낼 화면과 채널을 고른다.
받는 사람과 본문은 앞 대화나 조회 결과에서 가져온다. 팩스는 첨부가 필요해 지금은 못 보낸다
{"kind":"send","route":"/w_tb_e451","channel":"email","to":"a@b.co.kr","subject":"견적서 안내","body":"...","reason":"..."}
채널은 화면마다 다른 자리에 있다. **문자는 고장접수(/w_tb_e401)** 이고, 그 버튼은 먼저
문자유형을 묻는다 — 보통 문자는 fields 로 {"gubun":"0"} 을 같이 낸다. 메일은 견적서 등
문서 화면의 EMAIL 이고 **결재가 끝난 문서만** 나간다
{"kind":"send","route":"/w_tb_e401","channel":"sms","to":"01012345678","body":"...","fields":{"gubun":"0"},"reason":"..."}
보낼 화면에 자료가 올라와 있어야 보낼 것이 생긴다 — 기간·현장 같은 조회 조건을
\`conditions\` 로 함께 낸다. 조회 없이 누르면 "문자전송할 고장내역이 없습니다" 로 되돌아온다
**번호나 주소를 모르면 되묻지 마라.** ERP 가 이미 들고 있다 — 어디서 찾을지 \`toLookup\` 에
적으면 우리가 찾아서 보낸다. 현장 담당자 연락처는 보수현장현황(/w_tb_e601w)의 \`Acts\` 에
있고, 휴대폰은 \`hp\`, 사무실은 \`tel\` 이다. **그 화면이 아래 후보에 없어도 적어도 된다** —
연락처가 있는 화면은 이 질문의 후보로 뽑히지 않는다
{"kind":"send","route":"/w_tb_e401","channel":"sms","body":"...","fields":{"gubun":"0"},"toLookup":{"route":"/w_tb_e601w","rowsStateKey":"Acts","conditions":{"searchQuery":"덕봉빌딩"},"column":"hp"},"reason":"..."}

5. **결재함에 올라온 문서를 결재하라**는 요청이면("결재 올라온 거 승인해줘"·"김대리 견적 반려해, 단가
다시 보라고") approve 를 낸다. 화면은 미결함으로 정해져 있다. 어느 문서인지는 match 로 —
제목(title)·작성자(writer)·문서 종류(paper: 견적서·지출결의서·업무일지…)의 일부만 사람이 말한 대로.
말하지 않은 것은 넣지 마라(전부면 match 를 비운다). 반려는 사유를 remark 로. **바로 결재하지 않는다**
— 우리가 목록을 보여 주고 한 번 확인받는다
{"kind":"approve","action":"승인","match":{"paper":"견적서"},"reason":"..."}
{"kind":"approve","action":"반려","match":{"writer":"김민수"},"remark":"단가 다시 확인","reason":"..."}

6. **같은 일을 여러 건** 하라는 요청이면("23일 점검 전부 24일로", "다음 주 당직 월 김철수 화 이영희 …")
batch 로, items 에 **건마다** 2번의 prepare 계획을 하나씩 낸다. 건마다 다른 값만 다르고 모양은 같다.
우리가 목록을 보여 주고 한 번 확인받은 뒤 차례로 한다. 한 건이면 batch 를 쓰지 마라
{"kind":"batch","items":[{"kind":"prepare","route":"/w_tb_e511","calendarDay":"20261020","rows":{"stateKey":"workerPopupList","items":[{"perid":"김철수"}]},"fields":{},"reason":"..."},{"kind":"prepare","route":"/w_tb_e511","calendarDay":"20261021","rows":{"stateKey":"workerPopupList","items":[{"perid":"이영희"}]},"fields":{},"reason":"..."}],"reason":"..."}
건 하나의 모양은 그 일을 한 건만 할 때 내는 prepare 그대로다 — 스킬이 적은 모양을 따른다

7. **조작 방법**만 묻는 질문이면 그 화면만 고른다
{"kind":"howto","route":"/w_...","reason":"..."}

8. 무엇을 묻는지 **정말 모르겠으면** 되묻는다. 짐작해서 엉뚱한 화면을 열지 마라
{"kind":"ask","question":"어느 쪽을 말씀하시나요?","options":["자체점검 결과","자체점검 계획"],"reason":"..."}

되묻기는 **어느 화면인지** 모를 때만 쓴다. **기간은 절대 되묻지 마라** — 사람이 말하지
않았으면 오늘을 기준으로 네가 정한다(이번 달, 올해처럼 질문에 맞는 범위로). 사무직
직원에게 "조회할 기간을 알려 주세요" 는 답할 말이 없는 질문이고, 틀렸으면 그 사람이
"작년으로 다시" 라고 말하면 된다. 새 문서 작성을 요청받았으면 칸의 값을 묻지
말고 prepare 로 화면을 열어라. 계정과목·지급유형·지급은행처럼 화면에서 고르는 값은
아는 것만 넣고 나머지는 비워 둔다 — 화면을 연 뒤 실제 선택지를 그 자리에서 내민다.
사람은 화면을 보지 않은 채 그 목록을 알 수 없다.
목록에서 고르는 칸(콤보·검색 칸)은 값을 모르면 되묻지 말고 비운 채 내라 — 화면이 고르게 한다.
글자칸(자유 입력)은 지금처럼 되물어도 된다.
정말 화면이 애매해 되물어야 하겠으면, 그 칸이 있는 화면 경로를 "route" 에 함께 실어라.

되묻기는 아껴라. 화면을 고를 수 있으면 골라라 — 사용자는 답을 원하지 질문을 원하지 않는다.
${CACHE_SPLIT}
오늘 날짜: {today}

질문: {question}
{context}
화면 목록:
{screens}`;

export function parsePlan(
  body: string,
  screens: AgentScreen[],
  options?: { context?: PlanContext },
): QueryPlan {
  let parsed: { kind?: string; [key: string]: unknown };
  try {
    parsed = JSON.parse(body.trim().replace(/^```json\s*|\s*```$/g, ''));
  } catch {
    throw new Error(`계획이 JSON 이 아니다: ${body.slice(0, 120)}`);
  }
  const byRoute = new Map(screens.map((screen) => [screen.route, screen]));

  const reason = String(parsed.reason ?? '');
  if (parsed.kind === 'ask') {
    const question = String(parsed.question ?? '').trim();
    if (!question) throw new Error('되물을 말이 없다');
    const options = Array.isArray(parsed.options) ? parsed.options.map(String) : undefined;
    const route = typeof parsed.route === 'string' && byRoute.has(parsed.route)
      ? parsed.route : undefined;
    const field = typeof parsed.field === 'string' && parsed.field.trim()
      ? parsed.field.trim() : undefined;
    return { kind: 'ask', question, options, reason, route, field };
  }
  if (parsed.kind === 'batch') {
    const raw = Array.isArray(parsed.items) ? parsed.items : [];
    if (raw.length < 2) throw new Error('batch 는 두 건 이상이다 — 한 건이면 prepare 로 내라');
    if (raw.length > 200) throw new Error(`batch 가 ${raw.length}건이다 — 200건까지만 한 번에 한다`);
    const items = raw.map((item, index) => {
      if (!item || typeof item !== 'object' || (item as { kind?: unknown }).kind !== 'prepare') {
        throw new Error(`batch 의 ${index + 1}번째가 prepare 가 아니다`);
      }
      // 건마다 홑 계획과 같은 검사를 받는다 — 화면·칸 이름이 틀리면 여기서 걸린다.
      return parsePlan(JSON.stringify(item), screens, options) as Extract<QueryPlan, { kind: 'prepare' }>;
    });
    return { kind: 'batch', reason, items };
  }
  if (parsed.kind === 'approve') {
    const action = String(parsed.action ?? '');
    if (action !== '승인' && action !== '반려' && action !== '전결') throw new Error(`결재 동작 ${action} 은 승인·반려·전결이 아니다`);
    const raw = (parsed.match ?? {}) as Record<string, unknown>;
    const match: { title?: string; writer?: string; paper?: string } = {};
    for (const key of ['title', 'writer', 'paper'] as const) {
      const value = String(raw[key] ?? '').trim();
      if (value) match[key] = value;
    }
    const remark = String(parsed.remark ?? '').trim();
    return {
      kind: 'approve', action, reason,
      ...(Object.keys(match).length ? { match } : {}),
      ...(remark ? { remark } : {}),
    };
  }
  if (parsed.kind === 'howto') {
    const route = String(parsed.route ?? '');
    if (!byRoute.has(route)) throw new Error(`${route} 는 화면 목록에 없다`);
    return { kind: 'howto', route, reason };
  }
  if (parsed.kind === 'send') {
    const lookupOf = (
      raw: unknown,
      routes: Map<string, unknown>,
    ): { route: string; rowsStateKey: string; conditions: Record<string, string>; column: string } | null => {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
      const spec = raw as Record<string, unknown>;
      const at = String(spec.route ?? '');
      const stateKey = String(spec.rowsStateKey ?? '');
      const column = String(spec.column ?? '');
      // 어디서 찾을지 셋 중 하나라도 없으면 찾을 수 없다. 지어낸 값으로 보내지 않는다.
      // **후보 목록에 있는지는 따지지 않는다** — 연락처가 있는 화면은 이 질문의 후보로
      // 뽑히지 않는다. 없는 화면이면 조회가 실패하고 그때 되묻는다.
      if (!at.startsWith('/') || !stateKey || !column) return null;
      void routes;
      const conditions = spec.conditions && typeof spec.conditions === 'object' && !Array.isArray(spec.conditions)
        ? Object.fromEntries(Object.entries(spec.conditions as Record<string, unknown>)
          .map(([key, value]) => [key, String(value ?? '')]))
        : {};
      return { route: at, rowsStateKey: stateKey, conditions, column };
    };
    const route = String(parsed.route ?? '');
    if (!byRoute.has(route)) throw new Error(`${route} 는 화면 목록에 없다`);
    const channel = String(parsed.channel ?? '');
    if (!['email', 'sms', 'kakao', 'fax'].includes(channel)) {
      throw new Error(`보낼 수 없는 채널이다: ${channel}`);
    }
    return {
      kind: 'send',
      route,
      channel: channel as 'email' | 'sms' | 'kakao' | 'fax',
      ...(parsed.to ? { to: String(parsed.to) } : {}),
      ...(parsed.subject ? { subject: String(parsed.subject) } : {}),
      ...(parsed.body ? { body: String(parsed.body) } : {}),
      ...(lookupOf(parsed.toLookup, byRoute) ? { toLookup: lookupOf(parsed.toLookup, byRoute)! } : {}),
      ...(parsed.conditions && typeof parsed.conditions === 'object' && !Array.isArray(parsed.conditions)
        ? {
          conditions: Object.fromEntries(Object.entries(parsed.conditions as Record<string, unknown>)
            .map(([key, value]) => [key, String(value ?? '')])),
        }
        : {}),
      // 전송 창 앞의 창이 요구하는 값. 문자유형처럼 고르지 않으면 아무 일도 안 난다.
      ...(parsed.fields && typeof parsed.fields === 'object' && !Array.isArray(parsed.fields)
        ? {
          fields: Object.fromEntries(Object.entries(parsed.fields as Record<string, unknown>)
            .map(([key, value]) => [key, String(value ?? '')])),
        }
        : {}),
      reason,
    };
  }
  if (parsed.kind === 'sequence') {
    const route = String(parsed.route ?? '');
    const raw = Array.isArray(parsed.steps) ? parsed.steps : [];
    const steps = raw
      .map((one) => (one && typeof one === 'object' ? one as Record<string, unknown> : null))
      .filter((one): one is Record<string, unknown> => Boolean(one))
      .map((one, index) => {
        const fields = one.fields && typeof one.fields === 'object' && !Array.isArray(one.fields)
          ? Object.fromEntries(Object.entries(one.fields as Record<string, unknown>)
            .map(([key, value]) => [key, String(value ?? '')]))
          : undefined;
        const spec = one.rows && typeof one.rows === 'object' ? one.rows as Record<string, unknown> : null;
        const items = Array.isArray(spec?.items)
          ? (spec!.items as unknown[])
            .filter((item) => item && typeof item === 'object')
            .map((item) => Object.fromEntries(Object.entries(item as Record<string, unknown>)
              .map(([key, value]) => [key, String(value ?? '')])))
          : [];
        const stateKey = String(spec?.stateKey ?? '');
        return {
          ...(one.tab ? { tab: String(one.tab) } : {}),
          ...(fields && Object.keys(fields).length ? { fields } : {}),
          ...(stateKey && items.length
            ? {
              rows: {
                stateKey,
                items,
                ...(spec?.addLabel ? { addLabel: String(spec.addLabel) } : {}),
              },
            }
            : {}),
          // 저장을 안 적었으면 **저장한다.** 걸음을 나눈 이유가 탭마다 저장하는 것이라,
          // 빠뜨린 걸음을 안 저장하면 그 탭은 조용히 비어 남는다.
          save: one.save === false ? false : true,
          label: String(one.label ?? `${index + 1}번째`),
        };
      })
      // 아무것도 채우지 않는 걸음은 저장만 눌러 봐야 할 일이 없다.
      .filter((step) => step.fields || step.rows);
    if (!route.startsWith('/') || !steps.length) {
      throw new Error('걸음 계획이 비어 있다. route 와 steps 를 확인하라.');
    }
    return { kind: 'sequence', route, steps, reason };
  }

  if (parsed.kind === 'attach') {
    const route = String(parsed.route ?? '');
    // 문서 번호도 파일도 **화면이 안다.** 계획이 낼 것은 어느 화면인가뿐이다 —
    // `appnum` 을 내게 했더니 그 값을 알 길이 없어 아무도 안 고르는 갈래가 됐다.
    if (!route.startsWith('/')) throw new Error('첨부 계획에 화면이 없다.');
    return { kind: 'attach', route, reason };
  }

  if (parsed.kind === 'operate') {
    const route = String(parsed.route ?? '');
    if (!byRoute.has(route)) throw new Error(`${route} 는 화면 목록에 없다`);
    const button = String(parsed.button ?? '').trim();
    if (!button) throw new Error('누를 버튼 이름이 없다');
    const confirm = String(parsed.confirm ?? '').trim();
    const operateFields = parsed.fields && typeof parsed.fields === 'object' && !Array.isArray(parsed.fields)
      ? Object.fromEntries(Object.entries(parsed.fields as Record<string, unknown>)
        .map(([key, value]) => [key, String(value ?? '')]))
      : undefined;
    // 표 안 버튼이면 **표·칸·행**이 다 있어야 누를 수 있다. 하나라도 없으면 그냥
    // 이름으로 누르는 옛 길로 둔다 — 반쪽 지정으로 엉뚱한 행을 누르는 것보다 낫다.
    const askedRow = parsed.row && typeof parsed.row === 'object' && !Array.isArray(parsed.row)
      ? parsed.row as Record<string, unknown>
      : undefined;
    const rowMatch = askedRow?.match && typeof askedRow.match === 'object' && !Array.isArray(askedRow.match)
      ? Object.fromEntries(Object.entries(askedRow.match as Record<string, unknown>)
        .map(([key, value]) => [key, String(value ?? '')]))
      : undefined;
    const rowConditions = askedRow?.conditions && typeof askedRow.conditions === 'object'
      && !Array.isArray(askedRow.conditions)
      ? Object.fromEntries(Object.entries(askedRow.conditions as Record<string, unknown>)
        .map(([key, value]) => [key, String(value ?? '')]))
      : undefined;
    const operateRow = askedRow && rowMatch && Object.keys(rowMatch).length
      && String(askedRow.stateKey ?? '').trim() && String(askedRow.column ?? '').trim()
      ? {
        stateKey: String(askedRow.stateKey).trim(),
        column: String(askedRow.column).trim(),
        match: rowMatch,
        ...(Number.isInteger(Number(askedRow.nth)) ? { nth: Number(askedRow.nth) } : {}),
        ...(rowConditions && Object.keys(rowConditions).length ? { conditions: rowConditions } : {}),
      }
      : undefined;
    // 목록 버튼(전자지로생성)은 조회해 둔 목록의 고른 행에 대고 누른다. 표 이름이 없으면 버린다 —
    // 어느 표를 조회할지 모르는 채로 누르는 것은 옛 길과 같다.
    const askedList = parsed.list && typeof parsed.list === 'object' && !Array.isArray(parsed.list)
      ? parsed.list as Record<string, unknown>
      : undefined;
    const listConditions = askedList?.conditions && typeof askedList.conditions === 'object'
      && !Array.isArray(askedList.conditions)
      ? Object.fromEntries(Object.entries(askedList.conditions as Record<string, unknown>)
        .map(([key, value]) => [key, String(value ?? '')]))
      : undefined;
    // 문서 하나에 대고 누르는 단추면 그 문서를 목록에서 고른다(focus).
    const askedFocus = parsed.focus && typeof parsed.focus === 'object' && !Array.isArray(parsed.focus)
      ? parsed.focus as Record<string, unknown>
      : undefined;
    const strings = (value: unknown) => (value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, one]) => [key, String(one ?? '')])
        .filter(([, one]) => String(one ?? "").trim()))
      : {});
    const focusMatch = strings(askedFocus?.match);
    const focusConditions = strings(askedFocus?.conditions);
    const operateFocus = askedFocus && String(askedFocus.stateKey ?? '').trim() && Object.keys(focusMatch).length
      ? {
        stateKey: String(askedFocus.stateKey).trim(),
        match: focusMatch,
        ...(innerRow(askedFocus.then) ? { then: innerRow(askedFocus.then)! } : {}),
        ...(Object.keys(focusConditions).length ? { conditions: focusConditions } : {}),
      }
      : undefined;
    const operateList = askedList && String(askedList.stateKey ?? '').trim()
      ? {
        stateKey: String(askedList.stateKey).trim(),
        ...(listConditions && Object.keys(listConditions).length ? { conditions: listConditions } : {}),
        ...(askedList.check === true ? { check: true } : {}),
        ...(typeof askedList.checkKey === 'string' && askedList.checkKey.trim() ? { checkKey: askedList.checkKey.trim() } : {}),
        ...(askedList.match && typeof askedList.match === 'object' && !Array.isArray(askedList.match)
          && Object.keys(askedList.match).length
          ? { match: Object.fromEntries(Object.entries(askedList.match as Record<string, unknown>).map(([key, value]) => [key, String(value ?? '')])) }
          : {}),
      }
      : undefined;
    return {
      kind: 'operate',
      route,
      button,
      ...(operateList ? { list: operateList } : {}),
      ...(operateFocus ? { focus: operateFocus } : {}),
      ...(confirm || choiceOf(button, Boolean(operateFocus?.then))
        ? { confirm: confirm || choiceOf(button, Boolean(operateFocus?.then))! } : {}),
      ...(operateFields && Object.keys(operateFields).length ? { fields: operateFields } : {}),
      ...(parsed.selectKey ? { selectKey: String(parsed.selectKey) } : {}),
      ...(operateRow ? { row: operateRow } : {}),
      reason,
    };
  }
  if (parsed.kind === 'prepare') {
    const route = String(parsed.route ?? '');
    const screen = byRoute.get(route);
    if (!screen) throw new Error(`${route} 는 화면 목록에 없다`);
    // 고치는 계획은 신규를 누르지 않는다. 점검결과등록에는 `new` 가 아예 없어서
    // 신규를 요구하면 그 화면을 향한 계획이 **하나도 통과하지 못한다** — 실제로
    // `요청을 처리하지 못했습니다` 로 끝났다. 고칠 때 필요한 것은 저장이다.
    const editing = Boolean(parsed.edit && typeof parsed.edit === 'object');
    // 달력 화면은 `신규` 로 시작하지 않는다. **그 날짜 칸을 눌러** 그날치 창을 열고,
    // 창 안의 `추가` 로 행을 만든다 — 스케쥴관리가 그렇다.
    const byCalendar = Boolean(parsed.calendarDay);
    if (!editing && !byCalendar && !screen.actions?.includes('new')) {
      throw new Error(`${route} 에 신규 작업이 없다`);
    }
    // **저장 단추 없이 칸이 곧 반영되는 화면**은 스킬이 안다(`deferCells`). 점검계획등록에는
    // 저장이 없다 — 점검일자를 달력에서 고르는 것이 저장이다.
    const immediate = [...(options?.context?.allSkills ?? []), ...(options?.context?.skills ?? [])]
      .some((skill) => skill.deferCells && skill.preferredRoutes?.includes(route));
    if (editing && !immediate && !screen.actions?.includes('save')) throw new Error(`${route} 는 저장할 수 없다`);
    const fields = (parsed.fields ?? {}) as Record<string, string>;
    const allowed = new Set((screen.draftFields ?? []).map((field) => field.key));
    for (const key of Object.keys(fields)) {
      if (!allowed.has(key)) {
        // 표의 칸을 헤더 칸에 적는 일이 잦다 — 입금입력의 `chk` 가 그렇다. 어디에
        // 적어야 하는지까지 말해 줘야 다음 계획이 고쳐진다.
        const grid = screen.grids.find((one) => one.columns.some((column) => column.id === key));
        if (grid) {
          throw new Error(`${key} 는 ${grid.key} 표의 칸이다.`
            + ' 화면 칸이 아니라 edit 의 cells 로 내라');
        }
        // 조회 조건을 초안 칸에 적는 일도 잦다 — 입금입력의 거래처가 그렇다.
        if (screen.fields.some((field) => field.key === key)) {
          throw new Error(`${key} 는 조회 조건이다. edit 의 conditions 로 내라`);
        }
        throw new Error(`${screen.name} 에 ${key} 신규 입력칸이 없다`);
      }
      fields[key] = String(fields[key]);
    }
    let reuse: Extract<QueryPlan, { kind: 'prepare' }>['reuse'];
    if (parsed.reuse !== undefined) {
      const raw = parsed.reuse as Record<string, unknown>;
      const rowsStateKey = String(raw.rowsStateKey ?? '');
      const grid = screen.grids.find((candidate) => candidate.key === rowsStateKey);
      if (!grid) throw new Error(`${screen.name} 에 ${rowsStateKey} 과거 목록이 없다`);
      if (!raw.match || typeof raw.match !== 'object' || Array.isArray(raw.match)) {
        throw new Error('과거 신청 선택 조건이 없다');
      }
      const match = Object.fromEntries(Object.entries(raw.match as Record<string, unknown>)
        .map(([key, value]) => [key, String(value)]));
      const columns = new Set(grid.columns.map((column) => column.id));
      for (const key of Object.keys(match)) {
        if (!columns.has(key)) throw new Error(`${screen.name} ${rowsStateKey} 에 ${key} 열이 없다`);
      }
      if (!Array.isArray(raw.fields)) throw new Error('과거 신청에서 복사할 필드가 없다');
      const copyFields = raw.fields.map(String);
      for (const key of copyFields) {
        if (!allowed.has(key)) throw new Error(`${screen.name} 에 ${key} 신규 입력칸이 없다`);
      }
      reuse = { rowsStateKey, match, fields: copyFields };
    }
    const rowsPlan = parsed.rows as {
      stateKey?: unknown; items?: unknown; tab?: unknown; addLabel?: unknown;
    } | undefined;
    let rows: Extract<QueryPlan, { kind: 'prepare' }>['rows'];
    if (rowsPlan && typeof rowsPlan === 'object' && Array.isArray(rowsPlan.items) && rowsPlan.items.length) {
      const stateKey = String(rowsPlan.stateKey ?? '').trim();
      if (!stateKey) throw new Error('상세행을 넣을 표 이름이 없다');
      // 화면에 없는 표 이름을 지어내면 행이 하나도 안 늘고 초안이 통째로 실패한다.
      // 여기서 걸러야 계획 재시도가 고칠 기회를 갖는다(견적서에서 `content` 를 지어냈다).
      const screen = byRoute.get(route);
      const grid = screen?.grids.find((one) => one.key === stateKey);
      // **창 안의 표는 카탈로그에 없다.** 팝업 배열은 고르는 목록이라 보고 일부러
      // 빼 두었기 때문이다. 그런데 달력 화면(스케쥴관리)은 그 창이 **유일한 쓸 자리**다 —
      // 스킬이 그 이름을 계약으로 적어 두었으면 그것이 더 정확한 앎이다.
      const namedBySkill = (options?.context?.skills ?? [])
        .some((skill) => skill.rowsStateKey === stateKey && skill.preferredRoutes?.includes(route));
      // 탭 표도 쓸 자리다 — 매입상세(`buys`)는 탭에만 있고 머리와 함께 저장된다.
      const tabbed = Boolean(screen?.tabs?.some((one) => one.stateKey === stateKey));
      if (screen && !grid && !namedBySkill && !tabbed) {
        throw new Error(`${route} 에 ${stateKey} 표가 없다. 있는 표: ${
          screen.grids.map((one) => one.key).join(', ')}`);
      }
      // 없는 칸을 지어내면 행은 붙고 그 칸에서 멎는다 — 시간외근무보고서가 `endtime`
      // 으로 그랬다. 어느 칸이 있는지는 카탈로그가 안다.
      if (grid) {
        const columns = new Set(grid.columns.map((one) => one.id));
        const unknown = rowsPlan.items
          .flatMap((item) => Object.keys(item as Record<string, unknown>))
          .filter((key) => !columns.has(key));
        if (unknown.length) {
          throw new Error(`${stateKey} 표에 ${Array.from(new Set(unknown)).join(', ')} 칸이 없다.`
            + ` 있는 칸: ${grid.columns.map((one) => one.id).join(', ')}`);
        }
      }
      rows = {
        stateKey,
        items: rowsPlan.items.map((item) => Object.fromEntries(
          Object.entries(item as Record<string, unknown>).map(([key, value]) => [key, String(value ?? '')]),
        )),
        ...(rowsPlan.tab ? { tab: String(rowsPlan.tab) } : {}),
        ...(rowsPlan.addLabel ? { addLabel: String(rowsPlan.addLabel) } : {}),
      };
    }
    // 이미 있는 문서를 고치는 계획. 고를 목록과 조건은 카탈로그로 검증한다 — 없는
    // 표·열을 지어내면 아무것도 못 고르고 조용히 새 문서를 만들게 된다.
    let edit: Extract<QueryPlan, { kind: 'prepare' }>['edit'];
    if (parsed.edit && typeof parsed.edit === 'object' && !Array.isArray(parsed.edit)) {
      const raw = parsed.edit as Record<string, unknown>;
      const rowsStateKey = String(raw.rowsStateKey ?? '').trim();
      const grid = screen.grids.find((candidate) => candidate.key === rowsStateKey);
      if (!grid) {
        throw new Error(`${screen.name} 에 ${rowsStateKey} 목록이 없다.`
          + ` 있는 표: ${screen.grids.map((one) => one.key).join(', ')}`);
      }
      // 조건이 없어도 된다 — `미처리인 거 처리해줘` 처럼 사람이 현장을 말하지 않는
      // 일이 흔하다. 그때는 목록에 있는 것을 그대로 내밀어 고르게 한다.
      const rawMatch = raw.match && typeof raw.match === 'object' && !Array.isArray(raw.match)
        ? raw.match as Record<string, unknown>
        : {};
      const match = Object.fromEntries(Object.entries(rawMatch).map(([key, value]) => [key, String(value)]));
      const columns = new Set(grid.columns.map((column) => column.id));
      for (const key of Object.keys(match)) {
        if (!columns.has(key)) throw new Error(`${screen.name} ${rowsStateKey} 에 ${key} 열이 없다`);
      }
      let cells: Record<string, string> | undefined;
      if (raw.cells && typeof raw.cells === 'object' && !Array.isArray(raw.cells)) {
        cells = Object.fromEntries(Object.entries(raw.cells as Record<string, unknown>)
          .map(([key, value]) => [key, String(value ?? '')]));
        // 칸 이름은 여기서 막지 않는다. 화면이 칸에 붙인 이름과 카탈로그가 아는 상태
        // 키가 다른 표가 있다 — 점검결과의 `결과` 칸은 `result` 인데 값은 `state` 에
        // 담긴다. 없는 칸이면 드라이버가 `1행의 X 칸을 찾지 못했다` 로 정확히 말한다.
      }
      let editConditions: Record<string, string> | undefined;
      if (raw.conditions && typeof raw.conditions === 'object' && !Array.isArray(raw.conditions)) {
        editConditions = Object.fromEntries(Object.entries(raw.conditions as Record<string, unknown>)
          .map(([key, value]) => [key, String(value ?? '')]));
        const searchable = new Map(screen.fields.map((field) => [field.key, field]));
        for (const key of Object.keys(editConditions)) {
          const found = searchable.get(key);
          if (!found) throw new Error(`${screen.name} 에 ${key} 검색조건이 없다`);
          // 날짜칸은 여덟 자리다. 붙임표가 들어가면 조회가 조용히 0행으로 끝난다.
          if (found.widget === 'DateTextBox' && /^\d{4}-\d{2}-\d{2}$/.test(editConditions[key] ?? '')) {
            editConditions[key] = (editConditions[key] ?? '').replace(/-/g, '');
          }
        }
      }
      edit = {
        rowsStateKey,
        match,
        ...(cells && Object.keys(cells).length ? { cells } : {}),
        ...(raw.cellsStateKey ? { cellsStateKey: String(raw.cellsStateKey) } : {}),
        ...(raw.cellsMatch && typeof raw.cellsMatch === 'object' && !Array.isArray(raw.cellsMatch)
          ? {
            cellsMatch: Object.fromEntries(Object.entries(raw.cellsMatch as Record<string, unknown>)
              .map(([key, value]) => [key, String(value ?? '')])),
          }
          : {}),
        ...(editConditions && Object.keys(editConditions).length ? { conditions: editConditions } : {}),
        ...(innerRow(raw.then) ? { then: innerRow(raw.then)! } : {}),
      };
    }
    return {
      kind: 'prepare',
      ...(parsed.submit === true ? { submit: true } : {}),
      ...(parsed.calendarDay ? { calendarDay: String(parsed.calendarDay) } : {}),
      route,
      fields,
      reason,
      ...(reuse ? { reuse } : {}),
      ...(rows ? { rows } : {}),
      ...(edit ? { edit } : {}),
    };
  }

  const labels = new Set<string>();
  let requirements: DataRequirements | undefined;
  if (parsed.requirements !== undefined) {
    const raw = parsed.requirements as Partial<DataRequirements> | null;
    if (!raw || !Array.isArray(raw.dimensions) || !Array.isArray(raw.measures)
      || !Array.isArray(raw.operations)) {
      throw new Error('데이터 계약 requirements의 dimensions, measures, operations는 배열이어야 한다');
    }
    const requestedArtifact = String(raw.artifact ?? 'none').toLowerCase();
    type Artifact = NonNullable<DataRequirements['artifact']>;
    const artifact: Artifact = ({
      print: 'pdf', doc: 'docx', word: 'docx', excel: 'xlsx', ppt: 'pptx', powerpoint: 'pptx', slides: 'pptx',
    } as Record<string, Artifact>)[requestedArtifact]
      ?? requestedArtifact as Artifact;
    if (!['none', 'xlsx', 'csv', 'docx', 'pdf', 'pptx'].includes(artifact)) {
      throw new Error(`지원하지 않는 파일 형식이다: ${requestedArtifact}`);
    }
    requirements = {
      dimensions: raw.dimensions.map(String),
      measures: raw.measures.map(String),
      operations: raw.operations.map(String),
      artifact,
    };
  }

  const retrievals = (parsed.retrievals ?? []) as Retrieval[];
  // 앞선 조회가 내는 칸 — 펼쳐 읽기를 그 값으로 좁힐 때 있는 칸인지 본다.
  const columnsOf = new Map<string, Set<string>>();
  for (const retrieval of retrievals) {
    const screen = byRoute.get(retrieval.route);
    if (!screen) throw new Error(`${retrieval.route} 는 화면 목록에 없다`);
    if (labels.has(retrieval.label)) throw new Error(`${retrieval.label} 이라는 이름이 두 번 나왔다`);
    labels.add(retrieval.label);
    // 조건 없는 조회는 화면 기본값으로 조회한다는 뜻이다. 빠뜨린 채 두면 뒤에서 조건 글을 만들다
    // 「Cannot convert undefined or null to object」로 턴이 멎었다(인사등록 경력 펼쳐 읽기, 2026-10-21).
    if (!retrieval.conditions || typeof retrieval.conditions !== 'object' || Array.isArray(retrieval.conditions)) {
      retrieval.conditions = {};
    }

    let grid = screen.grids.find((g) => g.key === retrieval.rowsStateKey);
    if (!grid) {
      // 화면에 실제 상태 키가 하나뿐이면 모델이 관성적으로 쓴 `data`나 **아예 빠뜨린 것**을
      // 안전하게 교정한다. 서로 다른 표가 둘 이상이면 추측하지 않고 기존 오류를 낸다.
      //
      // 빠뜨린 것까지 고치는 까닭: 「거래처 몇 곳이야?」가 `거래처현황 에 undefined 표가
      // 없다. 있는 것: data` 로 걸려 계획을 다시 불렀다(2026-09-21). 오류 문구가 이미
      // 답을 말하고 있었다 — 표가 하나면 되물을 것이 없다. 되물음 한 번이 18k 자다.
      const keys = Array.from(new Set(screen.grids.map((candidate) => candidate.key)));
      if ((!retrieval.rowsStateKey || retrieval.rowsStateKey === 'data') && keys.length === 1) {
        retrieval.rowsStateKey = keys[0] as string;
        grid = screen.grids.find((candidate) => candidate.key === retrieval.rowsStateKey);
      }
    }
    if (!grid) {
      const keys = screen.grids.map((g) => g.key).join(', ');
      throw new Error(`${screen.name} 에 ${retrieval.rowsStateKey} 표가 없다. 있는 것: ${keys}`);
    }
    if (isSelectionGrid(grid.key)) {
      throw new Error(`${screen.name} 의 ${grid.key} 는 조회 결과가 아니라 필수 선택목록이다`);
    }
    if (retrieval.sweep !== undefined) {
      if (!grid.master || typeof retrieval.sweep !== 'object' || retrieval.sweep === null) {
        // 위 표에 걸었으면 어느 상세 표에 걸지 알려 준다 — 모르면 다음 계획이 sweep 을 빼고 상세를 한 건만 읽는다.
        const details = screen.grids.filter((candidate) => candidate.master?.stateKey === grid!.key)
          .map((candidate) => candidate.key);
        throw new Error(`${screen.name} 의 ${grid.key} 는 펼쳐 읽을 수 없다`
          + (details.length ? `. sweep 은 위 표가 아니라 상세 표에 건다 — rowsStateKey 를 상세 표 ${details.join(' 또는 ')} 로 두어라` : ''));
      }
      // 숫자만 남긴다 — 위 표 날짜 칸은 `20261015` 꼴이라 `2026-10-15` 를 그대로 넘기면 드라이버가 견주지 못한다.
      const bound = (value: unknown) => {
        if (value === undefined || value === null || value === '') return undefined;
        const digits = String(value).replace(/\D/g, '');
        if (digits.length !== 6 && digits.length !== 8) {
          throw new Error(`${screen.name} 의 sweep 범위 ${String(value)} 는 여섯 자리(YYYYMM)나 여덟 자리(YYYYMMDD) 날짜로 적어라`);
        }
        return digits;
      };
      const from = bound(retrieval.sweep.from);
      const to = bound(retrieval.sweep.to);
      const raw = retrieval.sweep.matchFrom as { label?: unknown; key?: unknown; column?: unknown } | undefined;
      let matchFrom: { label: string; key: string; column?: string } | undefined;
      if (raw !== undefined && raw !== null) {
        const label = String(raw.label ?? '');
        const key = String(raw.key ?? '');
        const column = raw.column === undefined || raw.column === null || raw.column === '' ? key : String(raw.column);
        if (label === retrieval.label || !labels.has(label)) {
          throw new Error(`${screen.name} 의 sweep.matchFrom 은 앞선 조회 이름이어야 한다: ${label}`);
        }
        const earlier = columnsOf.get(label);
        if (earlier?.size && !earlier.has(column)) {
          throw new Error(`앞선 조회 ${label} 에 ${column} 칸이 없어 sweep.matchFrom 으로 좁히지 못한다. 있는 칸: ${
            Array.from(earlier).join(', ')}`);
        }
        const masterColumns = screen.grids.find((candidate) => candidate.key === grid!.master!.stateKey)?.columns ?? [];
        if (!masterColumns.some((column) => column.id === key)) {
          throw new Error(`${screen.name} 의 위 표에 ${key} 칸이 없어 sweep.matchFrom 으로 좁히지 못한다. 있는 칸: ${
            masterColumns.map((column) => column.id).join(', ')}`);
        }
        matchFrom = { label, key, ...(column !== key ? { column } : {}) };
      }
      retrieval.sweep = {
        ...(grid.master.rangeKey && from ? { from } : {}),
        ...(grid.master.rangeKey && to ? { to } : {}),
        ...(matchFrom ? { matchFrom } : {}),
      };
    }
    // 펼쳐 읽으면 위 표 칸이 행마다 붙는다 — 필수 칸이 위 표에 있어도 된다.
    const swept = retrieval.sweep && grid.master
      ? screen.grids.find((candidate) => candidate.key === grid!.master!.stateKey)?.columns ?? []
      : [];
    columnsOf.set(retrieval.label, new Set([...grid.columns, ...swept].map((column) => column.id)));
    if (retrieval.requiredColumns !== undefined) {
      if (!Array.isArray(retrieval.requiredColumns) || retrieval.requiredColumns.length === 0) {
        throw new Error(`${screen.name} 조회의 requiredColumns가 비어 있다`);
      }
      retrieval.requiredColumns = retrieval.requiredColumns.map(String);
      let available = new Set([...grid.columns, ...swept].map((column) => column.id));
      // **열을 정적으로 알 수 없는 표가 있다.** 달력으로 그리는 화면(사전점검스케쥴)은
      // 머리줄이 없어 카탈로그의 열이 비어 있다. 그것을 "그 열이 없다" 로 읽으면 그
      // 화면은 무슨 계획을 세워도 막힌다 — 없는 것이 아니라 모르는 것이다.
      let missing = available.size
        ? retrieval.requiredColumns.filter((column) => !available.has(column))
        : [];
      // 화면은 맞는데 표를 잘못 골랐을 뿐일 수 있다. 월별입금현황은 현장별과
      // 거래처별을 나란히 두고 열 구성이 같다. 어느 표에 무엇이 있는지는 카탈로그에
      // 적혀 있으니 계획을 통째로 다시 세우게 할 이유가 없다.
      // **요구한 열을 전부 가진 표가 하나뿐일 때만** 바꾼다 — 여럿이면 어느 것이
      // 맞는지 모르고, 그때는 모델이 고르는 편이 낫다.
      if (missing.length) {
        const wanted = retrieval.requiredColumns;
        const fits = screen.grids.filter((candidate) => !isSelectionGrid(candidate.key)
          && wanted.every((column) => candidate.columns.some((have) => have.id === column)));
        if (fits.length === 1) {
          grid = fits[0];
          retrieval.rowsStateKey = grid!.key;
          available = new Set(grid!.columns.map((column) => column.id));
          missing = [];
        }
      }
      if (missing.length) {
        throw new Error(`${screen.name} 의 ${retrieval.rowsStateKey} 표에 필수 컬럼 ${missing.join(', ')} 이 없다. 있는 것: ${Array.from(available).join(', ')}`);
      }
    }
    for (const key of Object.keys(retrieval.conditions ?? {})) {
      const field = screen.fields.find((candidate) => candidate.key === key);
      if (field) {
        // 모델이 체크박스의 라벨을 그대로 값으로 돌려주는 구버전 응답도 체크로 읽는다.
        // "매출만"은 선택지가 아니라 resuck 체크박스를 켜라는 뜻이다.
        if (field.widget === 'CheckBox' && retrieval.conditions[key] === field.label) {
          retrieval.conditions[key] = '1';
        }
        // **날짜칸은 여덟 자리다.** 같은 계획이 어떤 화면에는 `20260831`, 어떤 화면에는
        // `2026-08-31` 을 넣었고, 붙임표가 든 쪽은 조회가 조용히 0행으로 끝났다 —
        // 사용자에게는 `조건에 맞는 자료가 없습니다` 로만 보인다.
        if (field.widget === 'DateTextBox' && /^\d{4}-\d{2}-\d{2}$/.test(retrieval.conditions[key] ?? '')) {
          retrieval.conditions[key] = (retrieval.conditions[key] ?? '').replace(/-/g, '');
        }
        continue;
      }
      const keys = screen.fields.map((field) => field.key).join(', ');
      throw new Error(`${screen.name} 에 ${key} 검색조건이 없다. 있는 것: ${keys}`);
    }
  }
  // 비어 있는 조회 계획은 대개 **갈래를 잘못 고른 것**이다. 문서를 써 달라는 말에
  // 조회 갈래로 답하려다 조회할 것이 없어 빈 목록을 내는 식이다 — `야근했어.
  // 시간외근무보고서 올려줘. 덕봉빌딩 고장 처리했어` 가 세 번 다 그랬다.
  // 되짚어 줄 때 그 사실을 알려 준다. 안 그러면 같은 빈 계획을 세 번 낸다.
  if (!retrievals.length) {
    throw new Error('조회 계획이 비어 있다.'
      + ' 문서를 만들어 달라는 말이면 kind 를 prepare 로 내라 —'
      + ' 조회할 것이 없는데 retrieve 로 답하면 아무것도 못 한다.');
  }
  return requirements
    ? { kind: 'retrieve', retrievals, reason, requirements }
    : { kind: 'retrieve', retrievals, reason };
}

type CapabilityRequirement = { dimension: RegExp; measure: RegExp };

/**
 * 질문이 명시한 분류축과 수치를 같은 결과 표에서 제공하는 화면을 찾는다.
 *
 * 벡터 검색은 화면 이름이 비슷한 "매출현황"을 잘 찾지만, 사업장 컬럼이 있는
 * 월매출매입현황을 후보 밖으로 밀 수 있다. 구조 계약은 이름 검색과 독립적으로
 * 적용해, 답을 만들 수 있는 화면이 모델의 화면 목록에서 사라지지 않게 한다.
 */
function capabilityRequirement(question: string): CapabilityRequirement | undefined {
  let dimension: RegExp | undefined;
  if (/(사업장|지점)/.test(question)) dimension = /(spjang(?:cd|nm)?|사업장|지점)/i;
  else if (/현장/.test(question)) dimension = /(actnm|projnm|현장)/i;
  else if (/부서/.test(question)) dimension = /(divinm|부서)/i;
  else if (/(사원|직원)/.test(question)) dimension = /(perid|pernm|사원|성명|직원)/i;

  let measure: RegExp | undefined;
  if (/매출/.test(question)) measure = /(misamt\d*|매출)/i;
  else if (/미수/.test(question)) measure = /(iamt\d*|resuamt\d*|미수)/i;
  else if (/(수익|이익)/.test(question)) measure = /(수익|이익|profit|compamt|phm_mamt)/i;
  else if (/연차/.test(question)) measure = /(restnum|연차.*잔여|잔여.*연차)/i;

  return dimension && measure ? { dimension, measure } : undefined;
}

function groupedGridMeanings(screen: AgentScreen): string[] {
  const byKey = new Map<string, string[]>();
  for (const grid of screen.grids) {
    if (isSelectionGrid(grid.key)) continue;
    const values = byKey.get(grid.key) ?? [];
    values.push(...grid.columns.flatMap((column) => [column.id, column.text]));
    byKey.set(grid.key, values);
  }
  return Array.from(byKey.values(), (values) => values.join(' '));
}

export function capabilityCandidates(question: string, screens: AgentScreen[]): AgentScreen[] {
  const requirement = capabilityRequirement(question);
  if (!requirement) return [];
  return screens.filter((screen) => usable(screen) && groupedGridMeanings(screen).some(
    (meaning) => requirement.dimension.test(meaning) && requirement.measure.test(meaning),
  )).slice(0, SHORTLIST);
}

/**
 * 이름 1등이 목록에서 잘렸으면 꼬리와 바꿔 넣는다.
 *
 * 자리를 앞에 만들지 않는다 — 검색이 뜻으로 고른 순서를 흔들면 잘 되던 질문이 달라진다.
 * 그저 **없어지지 않게** 한다.
 */
function withNamed(list: AgentScreen[], named: AgentScreen[]): AgentScreen[] {
  const best = named[0];
  if (!best || list.includes(best) || !list.length) return list;
  return [...list.slice(0, -1), best];
}

function uniqueScreens(groups: AgentScreen[][]): AgentScreen[] {
  const result: AgentScreen[] = [];
  for (const screen of groups.flat()) {
    if (!result.includes(screen)) result.push(screen);
    if (result.length >= SHORTLIST) break;
  }
  return result;
}

/**
 * 지금 보고 있는 화면과 열어 둔 화면들을 프롬프트에 넣을 모양으로 만든다.
 *
 * 화면 목록보다 **앞**에 들어간다. 뒤에 붙이면 이 지시가 화면 열두 개 뒤로 밀리고,
 * "JSON 만 출력한다" 보다도 뒤에 놓인다.
 *
 * 아는 것이 하나도 없으면 빈 문자열이다 — 그래야 이 기능을 쓰지 않는 쪽의 프롬프트가
 * 예전과 글자 하나까지 같다.
 */
export function describeContext(context: PlanContext | undefined): string {
  const tabs = context?.openTabs ?? [];
  const current = context?.currentRoute;
  const history = (context?.history ?? []).slice(-HISTORY_TURNS);
  const me = context?.me;
  const businessSites = context?.businessSites ?? [];
  const experiences = (context?.experiences ?? []).slice(0, 3);
  if (!current && tabs.length === 0 && history.length === 0 && !me
    && !businessSites.length && !experiences.length && !context?.skills?.length
    && !context?.capability) return '';

  const name = tabs.find((tab) => tab.path === current)?.name;
  const lines = [''];
  if (history.length) {
    lines.push('앞선 대화:');
    for (const turn of history) {
      lines.push(`  묻기: ${turn.question}`);
      lines.push(`  답하기: ${turn.answer}`);
    }
    lines.push('');
    lines.push('- "그거", "방금 것" 처럼 앞을 가리키면 위 대화를 보고 무엇인지 정한다');
    lines.push('- 앞선 질문의 기간·대상은 이어받되, 화면 경로는 자동으로 이어받지 않는다');
    // 사람은 기간을 한 번만 말한다. `올해 매출 성장세` 다음의 `수금 상황은?` 도 올해다.
    // 답변 끝의 `조회한 화면:` 줄에 그때 건 조건이 그대로 적혀 있다.
    lines.push('- 새 질문에 기간을 가리키는 말이 아예 없으면 앞선 답변의 `조회한 화면:` 줄에'
      + ' 적힌 기간을 그대로 쓴다. 기간을 임의로 오늘 하루나 이번 달로 좁히지 마라');
    lines.push('- 새 질문에 필요한 컬럼이 앞선 화면에 없으면 다른 화면과 원천 행을 다시 조회한다');
  }
  if (current) lines.push(`지금 보고 있는 화면: ${name ? `${name} (${current})` : current}`);
  if (tabs.length) lines.push(`열어 둔 화면: ${tabs.map((tab) => tab.name).join(', ')}`);
  if (me) {
    const site = me.spjangcd ? `, 사업장 ${me.spjangcd}${me.spjangnm ? ` ${me.spjangnm}` : ''}` : '';
    lines.push(`지금 묻는 사람: ${me.pernm} (사번 ${me.perid})${site}`);
    // **자기를 가리키는 말이 없으면 사번으로 좁히지 않는다.** 「이번 달 견적서 몇 건 썼어?」가 판마다
    // 사번을 걸었다 안 걸었다 해서 0건과 38건이 번갈아 나왔다(실제 테넌트, 2026-10-07 — 같은 물음 세 번에
    // 둘이 사번). 주어를 뺀 물음은 사무직이 회사 일을 묻는 말이다. 「우리」도 회사·팀이지 그 사람 하나가 아니다.
    lines.push('- "내", "제", "나" 처럼 자기를 가리키면 이 사번으로 조건을 채운다');
    lines.push('- 자기를 가리키는 말이 없으면(「몇 건 썼어?」처럼 주어를 뺀 물음, 「우리」) 사번·담당자·작성자 조건을 채우지 않는다');
    if (me.spjangcd) lines.push('- 사업장이 여럿인 회사에서 "우리 사업장" 은 위 사업장이다');
  }
  if (businessSites.length) {
    lines.push(`우측 상단 사업장 선택기: ${businessSites.map((site) => (
      `${site.code} ${site.name}${site.current ? '(현재)' : ''}`
    )).join(', ')}`);
    lines.push('- 사업장이 여러 개인 회사의 "지점별" 질문은 현재 사업장 한 곳의 결과를 전체 지점 결과로 답하지 않는다');
  }
  if (experiences.length) {
    lines.push('사용자가 맞다고 확인한 비슷한 실행 경험:');
    for (const experience of experiences) {
      lines.push(`  질문: ${experience.question}`);
      for (const recipe of experience.recipe) {
        lines.push(`  레시피: ${recipe.route} ${recipe.rowsStateKey}, 조건형식 ${JSON.stringify(recipe.conditionShape)}, 필수컬럼 ${recipe.requiredColumns.join(', ') || '없음'}`);
      }
    }
    lines.push('- 성공 경험은 강한 힌트지만 현재 화면 목록의 필드·표·컬럼 검증을 통과해야만 재사용한다');
  }
  lines.push(
    '',
    '- 질문이 "지금 이 화면"을 가리키면 그 화면으로 조회를 잡는다',
    // 열어 둔 화면으로 후보를 좁히면 아직 열지 않은 화면을 묻는 흔한 경우가 막힌다.
    '- 열어 둔 화면은 사용자가 무슨 일을 하는지 알려 주는 참고일 뿐이다. 후보를 그것으로',
    '  좁히지 마라. 어느 쪽인지 팽팽할 때만 기울여라',
  );
  return lines.join('\n')
    + describeSkills(context?.skills ?? [])
    + describeCapabilityTool(context?.capability);
}

/**
 * 앞선 대화에서 조회한 화면을 후보 앞에 세운다.
 *
 * 이어지는 질문에는 화면을 가리키는 말이 없다 — "금액이 큰 현장별로 정리해줘" 로는
 * 검색이 앞에서 쓴 화면을 못 찾는다. 그런데 프롬프트는 "아래 목록에 있는 화면만 쓴다"고
 * 말한다. **목록에 없으면 이어서 하라는 지시를 따를 방법이 없다.** 실제로 미수금을 잘
 * 조회해 놓고 이어지는 질문에서 다른 화면으로 넘어갔다.
 *
 * 판단을 더하지 않는다. 앞에서 실제로 조회한 것을 목록에 되돌려 놓을 뿐이고,
 * 무엇을 고를지는 그대로 모델이 정한다.
 */
export function withRecent(
  shortlist: AgentScreen[],
  context: PlanContext | undefined,
  screens: AgentScreen[],
  recentFirst = true,
): AgentScreen[] {
  const routes = [
    ...(context?.history ?? []).flatMap((turn) => turn.routes ?? []),
    ...(context?.experiences ?? []).flatMap((experience) => experience.recipe.map((item) => item.route)),
  ];
  if (!routes.length) return shortlist;

  const byRoute = new Map(screens.map((screen) => [screen.route, screen]));
  const recent: AgentScreen[] = [];
  // 최근 것부터. 같은 화면을 여러 번 썼으면 한 번만 올린다.
  for (const route of [...routes].reverse()) {
    const screen = byRoute.get(route);
    if (screen && usable(screen) && !recent.includes(screen)) recent.push(screen);
  }
  const rest = shortlist.filter((screen) => !recent.includes(screen));
  return (recentFirst ? [...recent, ...rest] : [...rest, ...recent]).slice(0, SHORTLIST);
}

/**
 * 모델이 새 계약 필드를 빠뜨린 구형 응답을 내도 명백히 틀린 화면은 실행하지 않는다.
 * 수익/이익은 특히 매출·수금·설치 건수와 혼동하기 쉬워, 직접 값 또는 검증된 원천
 * 두 값(공사금액, 매입단가)이 없으면 재계획시킨다.
 */
function validateQuestionCoverage(
  question: string,
  plan: QueryPlan,
  screens: AgentScreen[],
  context?: PlanContext,
): void {
  if (plan.kind !== 'retrieve') return;
  if (/(수익|이익)/.test(question)) {
    const labels = plan.retrievals.flatMap((retrieval) => {
      const screen = screens.find((candidate) => candidate.route === retrieval.route);
      const grid = screen?.grids.find((candidate) => candidate.key === retrieval.rowsStateKey);
      return (grid?.columns ?? []).flatMap((column) => [column.id, column.text]);
    }).join(' ');
    const direct = /(수익|이익)/.test(labels);
    const derivable = /(compamt|공사금액)/.test(labels) && /(phm_mamt|매입단가)/.test(labels);
    if (!direct && !derivable) {
      throw new Error('수익/이익을 계산할 컬럼이 없다. 수익·이익 컬럼 또는 공사금액과 매입단가가 같은 표에 필요하다');
    }
  }
  const contracted = context?.skills?.some((skill) => skill.kind === 'metric'
    && plan.retrievals.some((retrieval) => skill.preferredRoutes?.includes(retrieval.route)
      && (!skill.rowsStateKey || skill.rowsStateKey === retrieval.rowsStateKey)));
  // 선택된 업무 스킬은 화면·표·필수 컬럼을 더 강한 기계 계약으로 검증한다.
  // 일반 문구 정규식이 사원번호를 "직원 수"로 해석하지 못했다는 이유로 막지 않는다.
  if (contracted) return;
  const capability = capabilityRequirement(question);
  // 카탈로그에 계약을 충족하는 대안이 실제로 있을 때만 막는다. 일부 배포나
  // 권한에서는 그 화면 자체가 없을 수 있는데, 그때까지 모든 조회를 거부하면
  // 현재 가능한 최선의 결과조차 낼 수 없다.
  if (capability && capabilityCandidates(question, screens).length > 0) {
    const covered = plan.retrievals.some((retrieval) => {
      const screen = screens.find((candidate) => candidate.route === retrieval.route);
      if (!screen) return false;
      const sameKey = screen.grids.filter((grid) => grid.key === retrieval.rowsStateKey);
      const meaning = sameKey.flatMap((grid) => grid.columns.flatMap(
        (column) => [column.id, column.text],
      )).join(' ');
      const globalBusinessDimension = /(사업장|지점)/.test(question)
        && Boolean(context?.businessSites?.length)
        && Boolean(context?.skills?.some((skill) => skill.preferredRoutes?.includes(retrieval.route)));
      return (globalBusinessDimension || capability.dimension.test(meaning))
        && capability.measure.test(meaning);
    });
    if (!covered) {
      throw new Error('질문의 분류 기준 컬럼과 계산할 값 컬럼이 같은 결과 표에 없다');
    }
  }
}

/** 선택된 metric skill의 기계 계약을 LLM 권고가 아니라 실행 전 불변조건으로 만든다. */
/**
 * 상세행이 있어야 쓸모 있는 초안인가. **완화해도 이것만은 지킨다** — 행 없는 문서는
 * 저장이 반드시 막히고(`경고: 시간외근무 내역이 없습니다`), 다음 문서로도 못 잇는다.
 */
/**
 * **빈 행은 행이 아니다.** 값이 하나도 없는 상세행을 계획에서 걷어낸다.
 *
 * `requireDraftRows` 가 상세행을 무조건 요구하니, 사용자가 품목을 말하지 않은 물음에서
 * 모델은 규칙을 통과하려고 `[{pcode:"", qty:""}]` 같은 **빈 행**을 낸다. 그러면 런타임이
 * 그 빈 칸을 채우려고 「견적서에 넣을 자재 품목명과 수량을 알려 주세요」로 되묻고, 문서는
 * 만들어지지 않는다 — `견적서 하나 만들어줘` 가 이렇게 끝났다(2026-09-16 실측).
 *
 * **견적서는 자재행 없이도 저장된다.** 화면을 열어 머리만 채우고 저장해 확인했다
 * (`저장: 서버에 잘 저장했습니다`, 새 줄 하나). 그러니 사용자가 품목을 말하지 않았으면
 * 행 없이 만드는 것이 사람이 하는 일과 같다.
 *
 * 걷어낸 뒤 행이 하나도 안 남으면 `requireDraftRows` 는 **요구를 접는다** — 다시
 * 「행을 내라」고 하면 모델은 또 빈 행을 낸다. 행을 아예 안 낸 계획은 여전히 막는다
 * (그건 사용자가 품목을 말했는데 모델이 빠뜨린 자리다).
 */
function dropEmptyRows(plan: QueryPlan): void {
  if (plan.kind !== 'prepare' || !plan.rows?.items?.length) return;
  const filled = plan.rows.items
    .filter((item) => Object.values(item).some((value) => String(value ?? '').trim()));
  if (filled.length === plan.rows.items.length) return;
  if (filled.length) { plan.rows = { ...plan.rows, items: filled }; return; }
  delete plan.rows;
  plan.droppedEmptyRows = true;
}

function requireDraftRows(plan: QueryPlan, skills: AgentSkill[]): void {
  if (plan.kind !== 'prepare') return;
  // **이미 있는 문서의 칸을 고치는 것은 새 초안이 아니다.** 행을 요구하면 "교통비를
  // 4만원으로 고쳐줘" 가 계획 단계에서 죽는다 — 고칠 문서에는 행이 이미 있다.
  if (plan.edit && Object.keys(plan.edit.cells ?? {}).length) return;
  for (const skill of skills) {
    if (!skill.draftRows || !skill.rowsStateKey) continue;
    if (!skill.preferredRoutes?.includes(plan.route)) continue;
    if (plan.rows?.items.length) continue;
    // 빈 행을 걷어내서 없어진 것이면 요구하지 않는다 — 다시 요구하면 또 빈 행이 온다.
    if (plan.droppedEmptyRows) continue;
    throw new Error(`${plan.route} 초안은 ${skill.rowsStateKey} 상세행이 있어야 한다.`
      + ` rows 로 ${(skill.requiredColumns ?? []).join(', ') || '품목과 수량'} 을 넣어라`);
  }
}

/**
 * 이미 있는 문서를 고치는 업무인가. **신규를 누르면 같은 문서가 하나 더 생긴다.**
 */
function requireEdit(plan: QueryPlan, skills: AgentSkill[], continuing?: boolean): void {
  if (plan.kind !== 'prepare') return;
  for (const skill of skills) {
    if (!skill.editRows) continue;
    if (!skill.preferredRoutes?.includes(plan.route)) continue;
    if (plan.edit?.rowsStateKey === skill.editRows) {
      // 순서는 화면의 사정이다 — 모델에게 맡기지 않고 스킬이 정한다.
      if (skill.fieldsBeforeCells) plan.edit.fieldsFirst = true;
      // **고르는 순간 반영되는 칸은 확정으로 미룬다.** 준비에서 채우면 Auto 가 꺼져 있어도
      // 점검일자가 옮겨진다. 어느 표에서 고칠지도 스킬이 안다 — 창 안 표는 카탈로그에 없다.
      // **창에서 고르는 일은 칸을 받지 않는다.** 무엇을 누를지는 스킬이 안다 — 창 안 표와
      // 단추까지. 모델이 낸 칸(`chk`, `mismijchk`)은 버린다. 사람이 말한 창 안 줄만 `cellsMatch` 로 남는다.
      if (skill.editWindow) {
        plan.edit.window = skill.editWindow;
        if (skill.editCells) plan.edit.cellsStateKey = skill.editCells;
        delete plan.edit.cells;
        if (plan.edit.cellsMatch) {
          plan.edit.cellsMatch = Object.fromEntries(Object.entries(plan.edit.cellsMatch)
            .filter(([key, value]) => key !== skill.editWindow!.check && String(value ?? '').trim()));
          if (!Object.keys(plan.edit.cellsMatch).length) delete plan.edit.cellsMatch;
        }
      }
      if (skill.deferCells) {
        plan.edit.deferCells = true;
        if (skill.editCells) plan.edit.cellsStateKey = skill.editCells;
        // 날짜는 여덟 자리다. 붙임표가 들어가면 달력에서 그 날을 못 찾는다.
        plan.edit.cells = Object.fromEntries(Object.entries(plan.edit.cells ?? {})
          .map(([key, value]) => [key, /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.replace(/-/g, '') : value]));
        if (!Object.keys(plan.edit.cells ?? {}).length) {
          throw new Error(`${plan.route} 에서 무엇으로 바꿀지 edit 의 cells 로 내라 — 사용자가 말한 값이다`);
        }
      }
      // **칸을 고칠 표도 스킬이 정한다**(창도 미루기도 아닌 보통 고치기). 일근태등록은 날 목록
      // (`dayList`)에서 날을 고르고 직원 표(`dayDetailList`)의 칸을 고친다. 계획이 표 이름을 빼먹으면
      // 날 목록에서 사람을 찾다 못 찾는다.
      if (skill.editCells) plan.edit.cellsStateKey = skill.editCells;
      // 답한 턴에는 목록이 이미 화면에 있다. 조건을 또 요구하면 계획이 고른 행 이름을
      // 조회 조건에 밀어 넣는다 — `덕봉빌딩(두암정철)` 을 거래처로 찾으려 했다.
      const given = new Set(Object.keys(plan.edit.conditions ?? {}));
      const missing = continuing ? [] : (skill.editConditions ?? []).filter((key) => !given.has(key));
      if (missing.length) {
        throw new Error(`${plan.route} 에서 고칠 문서를 찾으려면 ${missing.join(', ')} 조건이 필요하다.`
          + ' edit 의 conditions 로 같이 내라 — 기본 조건은 이번 것뿐이라 지난 것은 목록에 없다');
      }
      continue;
    }
    throw new Error(`${plan.route} 은 이미 있는 문서를 고치는 화면이다.`
      + ` edit 으로 ${skill.editRows} 에서 그 행을 골라라 —`
      + ' 조건은 사람이 말한 현장·호기·날짜로 짠다. 번호를 사용자에게 묻지 마라');
  }
}

/** 화면이 반드시 요구하는 칸을 계획이 흘리지 않았는가. */
/**
 * **사용자가 말하지 않은 규격을 지어 붙이지 않는다.**
 *
 * 스킬 문장에 예시로 적힌 이름(`WIRE ROPE`)을 모델이 값으로 집어 갔고, 거기에 규격까지
 * 붙여 `WIRE ROPE 8mm X 2R` 을 냈다. 화면 목록에 진짜로 있는 이름이라 팝업도 통과했고,
 * `도어센서 2개` 를 부탁한 사람은 그 자재가 든 견적서를 받았다 — 아무 데서도 안 막혔다.
 *
 * 낱말을 통째로 요구하지는 않는다. 사람은 `로프` 라 말하고 화면은 `WIRE ROPE` 다.
 * 대신 **숫자가 든 토막**(`8mm`·`2R`·`4본`)은 규격이고, 규격은 사람이 말한 것이어야
 * 한다 — 그것이 다르면 다른 물건이다.
 */
function requireSpokenRows(plan: QueryPlan, spoken: string): void {
  if (plan.kind !== 'prepare' || !plan.rows?.items?.length) return;
  const pieces = (value: string): string[] => value
    .replace(/[^0-9A-Za-z가-힣]+/g, ' ')
    .split(/\s+/)
    .filter((piece) => piece.length >= 2 && !/^\d+$/.test(piece))
    .map((piece) => piece.toLocaleLowerCase());
  const said = new Set(pieces(spoken));
  for (const item of plan.rows.items) {
    for (const [column, value] of Object.entries(item)) {
      if (!/(cd|code|id)$/i.test(column)) continue;
      const invented = pieces(String(value ?? ''))
        .filter((piece) => /\d/.test(piece) && !said.has(piece));
      if (!invented.length) continue;
      throw new Error(`${value} 의 규격 ${invented.join(', ')} 는 사용자가 말하지 않은 것이다.`
        + ` ${column} 에는 사용자가 말한 이름을 그대로 넣어라 —`
        + ' 화면 목록에 없으면 화면이 실제 선택지를 알려 준다');
    }
  }
}

/**
 * **수량은 사람이 센 개수다.**
 *
 * `WIRE ROPE 일대일, 4본 한 개 불출해줘` 를 계획은 품명 `WIRE ROPE 일대일` + 수량 `4`
 * 로 갈랐다 — `4본` 은 규격의 일부인데 그것을 수량으로 옮긴 것이다. 스킬 문장으로
 * 못박아도 그대로였다. 사람이 개수를 한 번만 말했으면 그 수가 수량이다.
 */
const COUNT_WORDS: Record<string, string> = {
  한: '1', 두: '2', 세: '3', 네: '4', 다섯: '5', 여섯: '6', 일곱: '7', 여덟: '8', 아홉: '9', 열: '10',
};

function spokenCounts(spoken: string): string[] {
  const found = new Set<string>();
  // `` 는 한글 뒤에서 걸리지 않는다 — `한 개` 가 통째로 빠졌다.
  const pattern = /(\d+|한|두|세|네|다섯|여섯|일곱|여덟|아홉|열)\s*(개|대|ea|EA)/g;
  // `matchAll` 이 주는 것은 배열이 아니라 반복자다. 앱 쪽 tsconfig 는 그것을 그대로
  // 돌리지 못한다(`--downlevelIteration` 필요) — 배열로 받아 넘긴다.
  for (const match of Array.from(spoken.matchAll(pattern))) {
    const said = match[1] ?? '';
    found.add(COUNT_WORDS[said] ?? String(Number(said)));
  }
  return Array.from(found);
}

function requireSpokenCount(plan: QueryPlan, spoken: string): void {
  if (plan.kind !== 'prepare' || plan.rows?.items?.length !== 1) return;
  const counts = spokenCounts(spoken);
  if (counts.length !== 1) return;
  const item = plan.rows.items[0] ?? {};
  const planned = String(item.qty ?? '').trim();
  if (!planned || planned === counts[0]) return;
  throw new Error(`수량은 ${counts[0]} 이다. 사용자가 센 개수를 qty 로 내라 —`
    + ` 품명에 든 숫자(4본·8mm)는 규격이지 수량이 아니다 (지금 qty=${planned})`);
}

/**
 * **`내일` 은 오늘이 아니다.**
 *
 * `내일 하루 연차 쓸게` 에 계획이 오늘 날짜를 넣었다. 사람은 다음 날 쉬려고 올린
 * 문서가 오늘 날짜로 저장된 것을 나중에야 본다 — 결재까지 올라간 뒤다.
 *
 * 상대 날짜 말이 **하나만** 나온 발화에서만 본다. `어제 접수해서 오늘 처리` 처럼
 * 둘이 섞이면 어느 칸이 어느 날인지 여기서 알 수 없다.
 */
/**
 * 마지막 수단: **우리가 날짜를 고친다.**
 *
 * 모델이 두 번 다 못 고치면 턴이 통째로 죽는다 — 사용자는 `요청을 처리하지 못했습니다`
 * 만 받는다. 상대 날짜 말이 하나뿐인 발화에서 그 날이 어디에도 없으면, 오늘로 들어간
 * 칸(작성일자)은 그대로 두고 **다른 날짜 칸만** 그 날로 바꾼다.
 */
function repairSpokenDay(
  plan: QueryPlan,
  spoken: string,
  today: string,
  screens: AgentScreen[],
): QueryPlan {
  if (plan.kind !== 'prepare') return plan;
  const wanted = spokenDay(spoken, today);
  if (!wanted) return plan;
  const digits = (value: string) => value.replace(/-/g, '');
  const dated = (value: unknown) => /^\d{4}-?\d{2}-?\d{2}$/.test(String(value ?? '').trim());
  const all = [
    ...Object.values(plan.fields),
    ...(plan.rows?.items ?? []).flatMap((item) => Object.values(item)),
  ];
  if (all.some((value) => dated(value) && digits(String(value)) === digits(wanted))) return plan;
  // **문서를 만든 날은 오늘이 맞다.** 화면이 붙인 이름으로 가른다 — `작성일자`·`신청일자`
  // 는 오늘이고, `신청기간`·`사용일자`처럼 일이 일어난 날을 가리키는 칸이 그 날이다.
  const screen = screens.find((candidate) => candidate.route === plan.route);
  const madeOn = new Set((screen?.draftFields ?? [])
    .filter((field) => /^(작성|신청|등록|접수|결의|청구)일(자|시)$/.test(field.label ?? ''))
    .map((field) => field.key));
  const fix = (key: string, value: string) => (
    dated(value) && !madeOn.has(key) ? wanted.replace(/-/g, '') : value
  );
  return {
    ...plan,
    fields: Object.fromEntries(Object.entries(plan.fields).map(([key, value]) => [key, fix(key, value)])),
    ...(plan.rows
      ? {
        rows: {
          ...plan.rows,
          items: plan.rows.items.map((item) => Object.fromEntries(
            Object.entries(item).map(([key, value]) => [key, fix(key, value)]),
          )),
        },
      }
      : {}),
  };
}

/** 발화에 상대 날짜 말이 **하나만** 있으면 그 날. 없거나 여럿이면 없다. */
function spokenDay(spoken: string, today: string): string | undefined {
  const days: Record<string, number> = { 그저께: -2, 어제: -1, 오늘: 0, 내일: 1, 모레: 2 };
  const said = Object.keys(days).filter((word) => spoken.includes(word));
  if (said.length !== 1) return undefined;
  const shift = days[said[0]!]!;
  if (!shift) return undefined;
  const base = new Date(`${today}T00:00:00`);
  return new Date(base.getTime() + shift * 86400000).toLocaleDateString('en-CA');
}

function requireSpokenDay(plan: QueryPlan, spoken: string, today: string): void {
  if (plan.kind !== 'prepare') return;
  const wanted = spokenDay(spoken, today);
  if (!wanted) return;
  const digits = (value: string) => value.replace(/-/g, '');
  // 그 날은 헤더가 아니라 **행**에 실리기도 한다 — 시간외근무보고서의 작성일자는
  // 오늘이고 근무한 날은 행의 `stdate` 다. 행을 안 보면 멀쩡한 계획을 막는다.
  const dates = [
    ...Object.entries(plan.fields),
    ...(plan.rows?.items ?? []).flatMap((item) => Object.entries(item)),
  ].filter(([, value]) => /^\d{4}-?\d{2}-?\d{2}$/.test(String(value ?? '').trim()));
  if (!dates.length) return;
  if (dates.some(([, value]) => digits(String(value)) === digits(wanted))) return;
  // 오늘로 들어간 칸을 먼저 짚어 준다. 어느 칸이 문제인지 말해 주지 않으면 모델은
  // 두 번 다 못 고친다 — 실제로 그렇게 턴이 죽었다.
  const stale = dates.filter(([, value]) => digits(String(value)) === digits(today)).map(([key]) => key);
  throw new Error(`오늘은 ${today} 이고 ${spoken.match(/그저께|어제|오늘|내일|모레/)?.[0]} 은 ${wanted} 다.`
    + (stale.length ? ` ${stale.join(', ')} 가 오늘로 들어가 있다.` : '')
    + ` 그 일이 일어나는 날을 가리키는 칸은 ${wanted} 로 바꿔라.`
    + ' 작성일자·신청일자처럼 문서를 만든 날은 오늘 그대로 둔다');
}

function requireFields(plan: QueryPlan, skills: AgentSkill[], continuing?: boolean): void {
  if (plan.kind !== 'prepare' || continuing) return;
  for (const skill of skills) {
    if (!skill.requiredFields?.length) continue;
    if (!skill.preferredRoutes?.includes(plan.route)) continue;
    const given = new Set([...Object.keys(plan.fields), ...Object.keys(plan.edit?.cells ?? {})]);
    const missing = skill.requiredFields.filter((key) => !given.has(key));
    if (missing.length) {
      throw new Error(`${plan.route} 은 ${missing.join(', ')} 없이는 저장되지 않는다.`
        + ' 그 칸도 같이 내라 — 목록에서 고르는 칸이면 화면이 실제 선택지를 알려 준다');
    }
  }
}

/**
 * 코드꼴 값인가. 한글이 들어가면 이름이다.
 *
 * 드라이버의 같은 판정과 짝이다 — 거기서 코드꼴 값은 팝업을 건너뛰고 코드 칸에
 * 곧바로 박힌다. 그 길로 들어간 값은 **아무도 확인하지 않는다.**
 */
function codeShaped(value: string): boolean {
  return /^[0-9A-Za-z_-]{1,20}$/.test(value.trim());
}

/**
 * 팝업으로 고르는 칸에 **코드를 짓지 못하게** 한다.
 *
 * 코드꼴 값은 드라이버에서 팝업을 건너뛰고 코드 칸에 곧바로 박힌다. 목록이 있으면
 * 그 값이 목록 안인지 다른 검증이 이미 본다. **목록이 없으면 아무도 안 본다** —
 * 틀린 코드가 들어가도 짝인 이름 칸만 빈 채로 남을 뿐 화면은 아무 말도 하지 않는다.
 *
 * (2026-09-01 실측 메모: 인사등록 직위 `rspcd` 는 이 자리의 예가 **아니다.**
 * `TB_PZ001` 이 카탈로그에 있어 모델이 목록에서 골랐다. 처음에 「지어냈다」고 본 것은
 * 오진이었다. 이 검증기가 잡는 것은 목록을 **아무도 안 가진** 팝업 칸이다.)
 *
 * 이름을 넣으면 화면이 팝업으로 코드를 정하고, 없는 이름이면 **후보를 들고 거절한다** —
 * 그 길이 이미 있다. 그러니 이름을 넣게 한다.
 *
 * 세 가지가 다 맞을 때만 막는다. 넓히면 멀쩡한 값이 걸린다:
 *
 * - 그 칸에 **이름 짝**(`pairs`)이 있다 — 이름을 넣을 자리가 실제로 있다는 뜻이다
 * - 코드 목록이 **없다** — 있으면 목록 밖 값은 이미 다른 검증이 막는다(고장내용 `008`)
 * - 그 값이 **사용자 말에 없다** — 사람이 코드로 말했으면 그대로 쓰는 것이 맞다
 */
function requireNamesNotCodes(
  plan: QueryPlan,
  screens: AgentScreen[],
  question: string,
  lists?: CodeLists,
): void {
  if (plan.kind !== 'prepare' && plan.kind !== 'sequence') return;
  const screen = screens.find((one) => one.route === plan.route);
  if (!screen) return;
  const given: Record<string, string>[] = plan.kind === 'prepare'
    ? [plan.fields ?? {}, plan.edit?.cells ?? {}]
    : plan.steps.map((step) => step.fields ?? {});
  for (const fields of given) {
    for (const [key, value] of Object.entries(fields)) {
      const text = String(value ?? '').trim();
      if (!text || !codeShaped(text)) continue;
      const field = (screen.draftFields ?? []).find((one) => one.key === key);
      if (!field?.pairs || !field.searchPopup) continue;
      if (codeListFor(field, lists)) continue;
      if (question.includes(text)) continue;
      const label = field.label ?? key;
      throw new Error(`${label}(${key}) 에 코드 ${text} 를 넣었다.`
        + ' 그 칸의 코드 목록은 아무도 갖고 있지 않으므로 지어낸 값이다.'
        + ' 코드가 아니라 **사용자가 말한 이름 그대로** 넣어라 —'
        + ' 화면이 팝업으로 코드를 정하고, 없는 이름이면 실제 후보를 알려 준다');
    }
  }
}

/**
 * **스킬이 지목한 화면을 안 썼으면 고쳐 쓴다. 거절하지 않는다.**
 *
 * 예전에는 던졌고, 그러면 계획을 통째로 다시 세운다 — 20k 자짜리 프롬프트가 한 턴에
 * 세 번에서 다섯 번 나갔다(2026-09-22 실측). 그런데 고칠 재료는 이미 다 있다:
 * 스킬이 화면·결과표·필수 컬럼을 알고 있고, 이 함수는 **빠진 컬럼을 이미 그렇게
 * 합치고 있다**(아래 `missing` 자리).
 *
 * 조건은 **그 화면이 아는 키만** 남긴다. 다른 화면을 보고 만든 조건을 그대로 옮기면
 * 없는 칸을 채우려다 죽는다. 하나도 안 남으면 화면 기본값으로 조회한다 — 무엇으로
 * 조회했는지는 답변이 밝힌다.
 *
 * 고친 사실은 반드시 남긴다. 조용히 바꾸면 사람이 다른 화면의 답을 받고도 모른다.
 */
/**
 * 질문이 **모델이 고른 화면**을 이름으로 말하고 **요구된 화면**(스킬·업무 도구)은
 * 말하지 않았나.
 *
 * 둘 다 말했으면 요구 쪽이 더 구체적이라고 보고 예전대로 둔다. 점검계획처럼 모델이
 * 고른 형제 화면(점검결과등록)을 질문이 말하지 않은 경우는 여기 안 걸린다 — 그때는
 * 고쳐 쓰기가 맞다.
 */
function spokeOtherScreen(
  question: string,
  plan: QueryPlan & { kind: 'retrieve' },
  wanted: string[],
  screens: AgentScreen[],
): boolean {
  const nameOf = (route: string) => screens.find((screen) => screen.route === route)?.name;
  const planNames = plan.retrievals
    .map((one) => nameOf(one.route))
    .filter((name): name is string => Boolean(name) && namesScreen(question, name));
  if (!planNames.length) return false;
  // 요구된 화면은 모델 화면 이름이 차지한 자리 **밖에서** 말했을 때만 말한 것으로 센다.
  // 단, 요구된 화면 이름이 모델 화면 이름을 품는 경우(더 구체적)는 그대로 센다.
  const spokenWanted = wanted.some((route) => {
    const name = nameOf(route);
    if (!name) return false;
    const covering = planNames.filter((one) => !screenTopic(name).includes(screenTopic(one)));
    return namesScreen(question, name, covering);
  });
  return !spokenWanted;
}

/**
 * 고쳐 쓰기의 근거가 사람의 말에 있나. 그 스킬의 트리거나 그 스킬 화면의 이름을 말했어야 한다.
 * 상세행을 쓰는 스킬(`draftRows`)은 근거가 있어도 조회를 고쳐 쓰지 않는다 — 결과 표가 문서의
 * 상세 표다. 업무일지처럼 결과 표가 목록인 쓰기 스킬은 다른 스킬과 같이 본다.
 */
function groundsRewrite(spoken: string, skill: AgentSkill, screens: AgentScreen[]): boolean {
  if (skill.draftRows) return false;
  return spokenGrounds(spoken, skill.triggers ?? [], skill.preferredRoutes ?? [], screens);
}

/**
 * 사람의 말에 그 업무의 근거가 있나 — 트리거를 말했거나 그 화면 이름을 말했다.
 *
 * 화면 이름은 핵심 구절이 **세 글자 이상**일 때만 센다. `휴가현황` 의 핵심 구절은 `휴가` 라 「내 휴가
 * 신청 내역」도 휴가현황을 말한 것이 되어, 휴가신청서를 맞게 고른 계획이 휴가현황으로 고쳐 쓰였다
 * (실제 테넌트, 2026-10-05).
 */
function spokenGrounds(spoken: string, triggers: string[], routes: string[], screens: AgentScreen[]): boolean {
  const compact = spoken.replace(/\s+/g, '');
  if (triggers.some((trigger) => {
    const word = trigger.replace(/\s+/g, '');
    return word.length >= 2 && compact.includes(word);
  })) return true;
  return routes.some((route) => {
    const name = screens.find((screen) => screen.route === route)?.name;
    return Boolean(name) && screenTopic(name!).length >= 3 && namesScreen(spoken, name);
  });
}

function rewriteToSkillRoute(
  plan: QueryPlan & { kind: 'retrieve' },
  skill: AgentSkill,
  screens: AgentScreen[],
): boolean {
  // **애매하면 손대지 않는다.** 고쳐 쓰기는 모델이 고른 화면을 말없이 바꾸는 일이라,
  // 어느 것을 바꿔야 할지 분명할 때만 한다. 아니면 예전처럼 거절하고 다시 묻는다.
  //
  // 조회가 여럿이면 어느 것을 바꿀지 알 수 없다 — `retrievals[0]` 을 집으면 멀쩡한
  // 다른 조회를 덮는다(매출과 매입을 견주는 계획이 그런 모양이다).
  if (plan.retrievals.length !== 1) return false;
  // 스킬이 화면을 여럿 대면 어느 것이 이 물음의 답인지 모른다. 세 스킬이 그렇다
  // (장기미수 · 공사주간실적 · 민원현황, 2026-09-22 기준).
  if ((skill.preferredRoutes?.length ?? 0) !== 1) return false;
  const target = skill.preferredRoutes?.[0];
  const first = plan.retrievals[0];
  // 고칠 재료가 없으면 예전처럼 거절한다. 반쪽으로 바꾸면 더 나쁘다.
  if (!target || !first || !skill.rowsStateKey) return false;
  const screen = screens.find((candidate) => candidate.route === target);
  if (!screen) return false;
  const known = new Set(screen.fields.map((field) => field.key));
  const kept = Object.fromEntries(
    Object.entries(first.conditions).filter(([key]) => known.has(key)),
  );
  const before = `${first.route}/${first.rowsStateKey}(${Object.keys(first.conditions).join('+') || '조건 없음'})`;
  first.route = target;
  first.rowsStateKey = skill.rowsStateKey;
  first.conditions = kept;
  first.requiredColumns = Array.from(new Set([
    ...(skill.requiredColumns ?? []),
  ]));
  // eslint-disable-next-line no-console
  console.debug('[agent] 계획 고쳐 씀', `${skill.name}`, before, '->',
    `${first.route}/${first.rowsStateKey}(${Object.keys(kept).join('+') || '조건 없음'})`);
  return true;
}

function validateSkillCoverage(
  question: string,
  plan: QueryPlan,
  skills: AgentSkill[],
  screens: AgentScreen[] = [],
  /** 사람이 실제로 한 말. 화면 이름을 댔는지는 이것으로 본다 — `makePlan` 의 `spoken`. */
  spoken: string = question,
): void {
  // 상세행이 있어야 쓸모 있는 초안인데 헤더만 냈다. 프롬프트로 적어 두어도 실행마다
  // 갈렸다 — 견적서에 자재가 없어 자재청구로 못 이었고, 시간외근무보고서는
  // `경고: 시간외근무 내역이 없습니다` 로 저장이 막혔다.
  if (plan.kind === 'prepare') {
    requireDraftRows(plan, skills);
    return;
  }
  if (plan.kind !== 'retrieve' || /건수/.test(question)) return;
  for (const skill of skills.filter((candidate) => candidate.kind === 'metric')) {
    if (!skill.preferredRoutes?.length) continue;
    let retrieval = plan.retrievals.find((candidate) => skill.preferredRoutes?.includes(candidate.route));
    if (!retrieval && spokeOtherScreen(spoken, plan, skill.preferredRoutes ?? [], screens)) {
      // **사람이 화면 이름을 댔으면 그 화면이 답이다.** 스킬은 capability 로 뽑히는데,
      // 맞는 화면에 스킬이 없으면 가장 가까운 남의 스킬이 뽑힌다 — 「안전교육 일지
      // 목록 보여줘」에 교육보고서 초안 스킬이, 「등록된 비용 항목」에 매입 분석 스킬이
      // 뽑혀 모델이 맞게 고른 화면을 고쳐 쓰기가 끌어갔다(2026-09-23 실측).
      // 거절하면 되묻느라 계획이 또 나가므로 이번 계획에서 이 스킬만 내려놓는다.
      // eslint-disable-next-line no-console
      console.debug('[agent] 스킬 내려놓음 — 질문이 화면을 이름으로 말했다', skill.name,
        plan.retrievals.map((one) => one.route).join(','));
      continue;
    }
    if (!retrieval && !groundsRewrite(spoken, skill, screens)) {
      // **말에 근거가 없는 스킬로는 모델이 고른 화면을 바꾸지 않는다.** 맞는 화면에 스킬이
      // 없으면 capability 가 가장 가까운 남의 스킬을 뽑고, 고쳐 쓰기가 맞는 선택을 끌어갔다 —
      // 「올해 내 휴가 신청 내역」·「내가 상신한 결재 문서」가 통합문서로, 「오늘 직원들 업무
      // 내역」이 사원명부로 갔다(실제 테넌트, 2026-10-05). 셋 다 질문에 그 스킬의 말(트리거)도
      // 화면 이름도 없었다. 상세행을 쓰는 스킬은 결과 표가 문서의 상세 표라 조회를 거기로
      // 옮기면 안 된다 — 「○○ 견적서 찾아줘」가 견적서의 부품 표(dataMaterials)로 갔다(같은 날).
      // eslint-disable-next-line no-console
      console.debug('[agent] 스킬 내려놓음 — 말에 근거가 없어 고쳐 쓰지 않는다', skill.name,
        plan.retrievals.map((one) => one.route).join(','));
      continue;
    }
    if (!retrieval) {
      // 다시 묻지 말고 고쳐 쓴다. 못 고칠 때만 예전처럼 거절한다.
      if (!rewriteToSkillRoute(plan as QueryPlan & { kind: 'retrieve' }, skill, screens)) {
        throw new Error(`${skill.name} 스킬의 권장 화면 ${skill.preferredRoutes.join(', ')}을 쓰지 않았다`);
      }
      retrieval = plan.retrievals[0]!;
    }
    // 콜백 안에서는 `let` 의 좁힘이 풀린다 — 앱의 tsc 가 「undefined 일 수 있다」로 막았다.
    const picked = retrieval;
    // 화면마다 결과 표가 다른 스킬이 있다 — 업무일지는 화면 둘이 표 이름이 다르다.
    const rowsAt = skillRowsAt(skill, retrieval.route);
    if (rowsAt && retrieval.rowsStateKey !== rowsAt) {
      throw new Error(`${skill.name} 스킬의 결과 표는 ${rowsAt}이어야 한다`);
    }
    const missing = (skill.requiredColumns ?? []).filter(
      (column) => !picked.requiredColumns?.includes(column),
    );
    if (missing.length) {
      // 스킬 카탈로그 자체가 화면 계약 검증을 통과한 컬럼이다. 모델이 일부를
      // 생략해도 고정 계산 스크립트에는 전부 필요하므로 실행 계약에 합친다.
      retrieval.requiredColumns = Array.from(new Set([...(retrieval.requiredColumns ?? []), ...missing]));
    }
    const dimensions = plan.requirements?.dimensions ?? [];
    const missingDimensions = (skill.requiredDimensions ?? []).filter((required) => (
      !dimensions.some((dimension) => dimension.includes(required))
    ));
    if (missingDimensions.length) {
      throw new Error(`${skill.name} 스킬의 필수 분류축 ${missingDimensions.join(', ')}을 계획에 넣지 않았다`);
    }
    // 「질문이 이럴 때만」은 **사람이 한 말**에 댄다. task 가 다시 쓴 문장에는 「전체」 같은
    // 말이 끼어들 수 있다 — 화면 이름 판정과 같은 이유다(`spoken`).
    const appliesConditions = !skill.conditionWhen || new RegExp(skill.conditionWhen).test(spoken);
    let wrongConditions = Object.entries(appliesConditions ? (skill.conditions ?? {}) : {}).filter(
      ([key, value]) => picked.conditions[key] !== value,
    );
    // **거절하지 말고 채워 넣는다.** 스킬이 값을 아는 자리다 — 화면 고쳐 쓰기와 같은 원칙이다.
    // 거절하면 20k 자짜리 계획이 다시 나가고, 모델은 또 다른 값을 낼 수 있다. 「유지보수 계약
    // 현황」이 계약분류 전체(8,810행)로 조회해 3분이 걸렸다 — 계약 중인 것은 15초다(2026-09-24).
    // 그 화면에 없는 칸이면 채울 수 없으니 예전처럼 거절한다.
    const fields = new Set((screens.find((one) => one.route === retrieval!.route)?.fields ?? []).map((f) => f.key));
    const fillable = wrongConditions.filter(([key]) => fields.has(key));
    if (fillable.length) {
      for (const [key, value] of fillable) retrieval.conditions[key] = value as string;
      // eslint-disable-next-line no-console
      console.debug('[agent] 고정 조건 채움', skill.name, fillable.map(([key, value]) => `${key}=${value}`).join(','));
      wrongConditions = wrongConditions.filter(([key]) => !fields.has(key));
    }
    if (wrongConditions.length) {
      throw new Error(`${skill.name} 스킬의 고정 조건 ${wrongConditions.map(
        ([key, value]) => `${key}=${value}`,
      ).join(', ')}을 적용하지 않았다`);
    }
  }
}

/**
 * 계획이 "오늘" 로 삼는 날짜. **로컬 달력 기준이다.**
 *
 * `toISOString()` 은 UTC 라, 한국에서 자정부터 오전 9시 사이에는 어제 날짜가 나온다.
 * 그 아홉 시간 동안 "내일 연차" 가 오늘 날짜로 작성됐다 — 실제로 본 것이다.
 */
export function localToday(now: Date = new Date()): string {
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

/**
 * 저장된 경험이 지금 질문과 정말 겹치는가.
 *
 * 검색 서버는 그 회사의 경험 중 가까운 것을 세 개까지 주는데, **하한이 없다.**
 * 경험이 입퇴사 하나뿐이던 날, 매출·발주·고장 질문의 계획 프롬프트에도 그것이
 * 실렸다(2026-08-12 보고서 여덟 개 실행). 경험은 "강한 힌트" 라고 적어 둔 것이라
 * 엉뚱한 화면 쪽으로 당길 수 있다.
 *
 * 한 글자 토막과 숫자만 있는 토막은 뺀다 — `2026`, `수` 같은 것이 겹쳐 봐야
 * 같은 업무라는 뜻이 아니다.
 */
export function shares(question: string, saved: string): boolean {
  const words = (value: string): Set<string> => new Set(
    value.replace(/[^0-9A-Za-z가-힣]+/g, ' ').split(/\s+/)
      .filter((word) => word.length >= 2 && !/^\d+$/.test(word)),
  );
  const wanted = words(question);
  const stored = words(saved);
  if (!wanted.size || !stored.size) return true;
  const shared = Array.from(wanted).filter((word) => stored.has(word)).length;
  return Math.max(shared / wanted.size, shared / stored.size) >= 0.25;
}

/**
 * 문서에서 읽은 값을 계획에게 알린다.
 *
 * **사용자가 말한 것과 같이 다루라고 못박는다.** 그러지 않으면 계획이 「발화에 없다」로
 * 보고 버린다 — 그 판단이 다른 자리에서는 옳으므로(지어내지 않기) 여기서만 예외를
 * 분명히 한다.
 */
function describeKnownFacts(facts: { label: string; value: string }[]): string {
  if (!facts.length) return '';
  const lines = facts.map((one) => `- ${one.label}: ${one.value}`).join('\n');
  return `\n\n사람이 올린 문서에서 읽은 값이다. **사용자가 직접 말한 것과 똑같이 다뤄라 — 발화에 없다고 버리지 마라.**\n${lines}`;
}

/**
 * **쓰기는 내민 목록 안에서만 한다.**
 *
 * 프롬프트는 「아래 목록에 있는 화면만 쓴다」고 말하는데 아무도 강제하지 않았다 —
 * `parsePlan` 의 검증은 **전체 카탈로그**(282개)에 그 이름이 있는지만 본다. 그래서
 * 매입등록을 시켰을 때 목록에 없던 **입고등록**으로 가서 거기에 문서를 만들려 했다
 * (실측 2026-09-15). 기대 화면은 목록에 있었다.
 *
 * 조회는 그대로 둔다 — 틀리면 다시 조회하면 그만이고, 검색이 목록 밖에서 맞는 화면을
 * 찾아 주는 길도 있다. **쓰기는 못 되돌린다.** 엉뚱한 화면에 문서를 만들거나 남의 문서를
 * 덮으면 돌려놓을 값이 아무 데도 없다. 목록에 없으면 다시 계획하게 한다 — 이 문구를
 * `index.ts` 의 재계획 갈래가 알아본다.
 *
 * 쓰기 21건을 재 보니 전부 이름 후보 안에 든다(실제 목록은 그보다 넓다). 맞는 화면이
 * 목록 밖으로 밀리면 쓰기가 막히는데, 그때는 **막히는 편이 낫다**.
 */
export function offListWrite(plan: QueryPlan, shortlist: AgentScreen[]): void {
  if (plan.kind !== 'prepare') return;
  if (shortlist.some((screen) => screen.route === plan.route)) return;
  throw new Error(`${plan.route} 는 화면 목록에 없다`);
}

/**
 * 되물어도 소용없는 실패인가.
 *
 * 한도(429)·서버 오류(5xx)·취소가 그렇다. 검증이 걷어찬 것과 **원인이 다르다** —
 * 그쪽은 같은 말을 다시 시키면 고쳐질 수 있지만 이쪽은 아니다.
 */
function hopeless(error: unknown): boolean {
  if ((error as { name?: string } | null)?.name === 'AbortError') return true;
  const said = error instanceof Error ? error.message : String(error ?? '');
  return /HTTP (429|5\d\d)|daily AI call limit/.test(said);
}

/**
 * 검색에 쓸 질문과 이 물음에 겹치는 실행 경험.
 *
 * 검색이 아무것도 못 찾으면 이름 겹침으로 떨어진다. 후보를 비우면 고를 기회 자체가 없어지고, 그것은
 * 틀리게 고르는 것보다 나을 게 없다. 선택지 답변("90일 이상")만 검색하면 원래 업무("장기 미수 계약중
 * 현장")가 사라진다. 최근 사용자 질문을 함께 검색하되 계획 프롬프트의 대화 표시는 그대로 둔다.
 */
function selection(question: string, context: PlanContext | undefined): {
  selectionQuestion: string; relevant: NonNullable<PlanContext['experiences']>;
} {
  const selectionQuestion = [
    ...(context?.history ?? []).slice(-3).map((turn) => turn.question),
    question,
  ].join('\n');
  // 겹침은 **사람이 한 말로만** 잰다. 작업이 붙은 질문에는 `지금까지:`·`조건:`·`처리:`
  // 가 딸려 오는데, 그 줄들이 실행마다 달라져 같은 질문이 어떤 실행은 `0/1`, 어떤
  // 실행은 `1/1` 로 갈렸다(2026-08-12 실측). 판정이 흔들리면 재사용도 흔들린다.
  const spoken = selectionQuestion.split('\n')
    .filter((line) => !/^(지금까지|조건|구분|처리|산출물)\s*:/.test(line.trim()))
    .join('\n');
  const relevant = (context?.experiences ?? []).filter((experience) => shares(spoken, experience.question));
  return { selectionQuestion, relevant };
}

/** 계획에 보일 화면 목록 — 스킬·구조·검색·이름 겹침 순. */
async function shortlistFrom(
  selectionQuestion: string,
  screens: AgentScreen[],
  found: AgentScreen[],
  context: PlanContext | undefined,
  hasRelevant: boolean,
): Promise<AgentScreen[]> {
  const structural = capabilityCandidates(selectionQuestion, screens);
  const skilled = await ranked(
    (context?.skills ?? []).flatMap((skill) => skill.preferredRoutes ?? []),
    screens,
    true,
  );
  // **이름이 그대로 들어맞는 화면은 자리를 보장한다.**
  //
  // 목록은 열두 개에서 자르는데 순서가 `[스킬, 구조, 검색, 이름겹침]` 이다. 검색이
  // 열둘을 채우면 이름 겹침이 **통째로 잘린다** — `이번 달 전자세금계산서 발행 내역
  // 보여줘` 에 `/w_tb_ia090(전자세금계산서발행)` 이 그렇게 사라졌고, 계획은 「화면
  // 목록에 없습니다」라고 답하거나 남은 것 중 아무거나 골라 인사등록에서 사원을
  // 만들려 했다(실측 2026-09-02, 세 판 내리 같다).
  //
  // 검색의 의미 순서는 지킨다. 이름 1등 하나만 꼬리와 바꾼다.
  const named = candidates(selectionQuestion, screens);
  // 업무 용어가 가리킨 화면은 스킬 다음 자리다 — 사람이 쓴 말의 뜻을 아는 쪽이 검색보다 확실하다.
  const termed = await ranked(context?.termRoutes ?? [], screens, true);
  const searched = withNamed(uniqueScreens([skilled, termed, structural, found, named]), named);
  // 검색 서버가 새 질문에 맞는 화면을 찾았다면 그 의미 순서를 지킨다. 검색이
  // 비어 "그거"를 해석할 단서가 없을 때만 최근 화면을 맨 앞으로 복원한다.
  const wide = withRecent(
    searched,
    context,
    screens,
    found.length === 0 || hasRelevant,
  );
  // **모델이 본 목록과 검사하는 목록을 같게 둔다.** 설명은 셋만 붙여 놓고 열둘로
  // 검사하면, 모델이 못 본 화면을 지어내도 `offListWrite` 가 통과시킨다.
  return wide.slice(0, promptScreens());
}

/**
 * **계획이 볼 화면 목록을 계획 없이 고른다.** 되묻기 전 추론(`reasoning.ts`)이 첫 계획 전에 돌 때 쓴다 —
 * 계획과 같은 화면을 봐야 같은 길을 짠다.
 */
export async function planScreens(
  question: string,
  screens: AgentScreen[],
  options: { shortlist?: Shortlist; context?: PlanContext } = {},
): Promise<AgentScreen[]> {
  const { selectionQuestion, relevant } = selection(question, options.context);
  const found = options.shortlist
    ? await ranked(await options.shortlist(selectionQuestion), screens)
    : [];
  // 계획처럼 겹치는 경험만 남긴다 — 무관한 경험의 화면이 앞에 올라오지 않게.
  const context = options.context && { ...options.context, experiences: relevant };
  return shortlistFrom(selectionQuestion, screens, found, context, relevant.length > 0);
}

export async function makePlan(
  question: string,
  screens: AgentScreen[],
  chat: Chat,
  options: {
    shortlist?: Shortlist; today?: string; context?: PlanContext; codeLists?: CodeLists;
    /**
     * **사람이 실제로 한 말.** `question` 은 대개 task 단계가 다시 쓴 문장이다(목표·조건·
     * 처리를 줄줄이 붙인다). 화면을 이름으로 댔는지를 그것으로 보면, 「처리: 교육 기록
     * 조회」의 `교육` 을 「교육보고서도 말했다」로 센다 — 실제 테넌트에서 안전교육일지가
     * 그렇게 교육보고서로 끌려갔다(2026-09-23). 없으면 `question` 으로 본다.
     */
    spoken?: string;
    /** 모델에게 보인 화면 목록을 받는다. 되묻기 전 추론(`reasoning.ts`)이 같은 화면을 봐야 같은 길을 짠다. */
    onShortlist?: (screens: AgentScreen[]) => void;
  } = {},
): Promise<QueryPlan> {
  const today = options.today ?? localToday();
  const { selectionQuestion, relevant } = selection(question, options.context);
  const found = options.shortlist
    ? await ranked(await options.shortlist(selectionQuestion), screens)
    : [];
  // 경험 재사용이 도는지 아무도 본 적이 없다. 프롬프트에 실제로 무엇이 실렸는지
  // 여기서만 알 수 있다 — 로컬(사용자가 `맞아요` 를 누른 것)과 검색 서버가 준 것이
  // 합쳐진 뒤이고, 프롬프트를 만들기 직전이다. 0 이면 재사용은 안 도는 것이다.
  const carried = options.context?.experiences ?? [];
  // eslint-disable-next-line no-console
  console.debug('[agent] 경험', `${relevant.length}/${carried.length}`, relevant.slice(0, 3).map((experience) => (
    `${experience.question} → ${experience.recipe.map((item) => item.route).join(',')}`
  )).join(' | ') || '없음');
  // **레시피 확정.** 사람이 맞다고 한 실행이 이 물음과 겹치면 모델에 묻지 않고 그대로
  // 쓴다. `conditionShape` 의 날짜는 레시피 당시 값이다 — 「이번 달」을 오늘로 옮기는
  // 것은 재측정에서 필요가 확인되면 다음 스펙이다.
  if (options.context?.confirmRecipes) {
    // 조건이 자리표(`{year-month}`·`{selected-value}`, `conditionShape`)인 레시피는 값이 아니라 형식이다 —
    // 그대로 넣어 「선택지 {selected-value} 을 찾지 못했다」로 끝났다(2026-10-23). 그런 것은 모델이 채운다.
    const proven = relevant.find((one) => one.validation?.valid && one.recipe.length
      && one.recipe.every((item) => Object.values(item.conditionShape).every((value) => !/^\{.+\}$/.test(value))));
    if (proven) {
      return {
        kind: 'retrieve',
        reason: `검증된 실행 경험: ${proven.question}`,
        retrievals: proven.recipe.map((item, index) => ({
          label: index === 0 ? '경험' : `경험${index + 1}`,
          route: item.route,
          rowsStateKey: item.rowsStateKey,
          conditions: { ...item.conditionShape },
          ...(item.requiredColumns?.length ? { requiredColumns: [...item.requiredColumns] } : {}),
        })),
      };
    }
  }
  const context = carried.length === relevant.length
    ? options.context
    : { ...options.context, experiences: relevant } as PlanContext;
  const shortlist = await shortlistFrom(selectionQuestion, screens, found, context, relevant.length > 0);
  options.onShortlist?.(shortlist);
  // 못 알아들은 말이 검색에서 놓친 것인지, 후보에는 있었는데 계획이 안 고른 것인지 가른다(2026-10-23).
  // eslint-disable-next-line no-console
  console.debug('[agent] 후보 화면', `검색 ${found.map((screen) => screen.name).join(',') || '없음'}`,
    `| 실음 ${shortlist.map((screen) => screen.name).join(',')}`);
  const writeRoutes = new Set(
    (options.context?.skills ?? [])
      .filter((skill) => skill.writes)
      .flatMap((skill) => skill.preferredRoutes ?? []),
  );
  const prompt = PLAN_PROMPT
    .replace('{today}', today)
    .replace('{question}', question)
    .replace('{context}', describeContext(context))
    // 코드 목록은 **쓰는 턴의 그 화면**에만 싣는다. `prepare` 인지로 게이트할 수 없다 —
    // 그것은 모델이 이 프롬프트를 받고 나서 내는 결과다. 대신 스킬 선택은 조립 전에
    // 끝나 있고, 쓰기 스킬은 전부 권장 화면을 갖고 있다.
    .replace('{screens}', shortlist.map((screen) => describe(
      screen,
      writeRoutes.has(screen.route) ? options.codeLists : undefined,
    )).join('\n\n'))
    // 파일은 이 메시지에 실려 가지만, **거기 무엇이 왔는지와 어떻게 다룰지**는 말로
    // 알려야 한다. 안 그러면 흐린 스캔에서 그럴듯한 값을 지어낸다. 프롬프트 **끝**에
    // 붙인다 — 화면 설명 안에 끼면 그 화면의 안내문으로 읽힌다.
    .concat(describeUploads(options.context?.files ?? []))
    .concat(describeKnownFacts(options.context?.facts ?? []));
  // 올린 파일은 **계획 호출마다** 함께 간다. 첫 판이 검증에 걸려 다시 낼 때 파일이
  // 빠지면 모델은 값을 어디서 봤는지 잃고, 앞 판에서 읽은 것을 기억으로 지어낸다.
  const files = options.context?.files ?? [];
  const uploads = files.length ? { files } : {};
  let validation = '';
  let lastError: unknown;
  /**
   * **모델이 거절한 스킬은 내려놓는다.**
   *
   * `validateSkillCoverage` 는 뽑힌 스킬의 권장 화면을 안 쓰면 계획을 걷어찬다. 그런데
   * 스킬은 트리거 낱말 하나로도 뽑힌다 — 「거래처 몇 곳이야?」에 `summarize-vendor-payables`
   * (거래처별 미지급)가 뽑혀 미지급현황 화면을 요구했다. 모델은 옳게 거절하는데 우리가
   * 같은 것을 다시 요구해서, 그 한 물음에 계획을 **다섯 번** 불렀다(105,813자, 2026-09-21).
   *
   * 한 번 거절한 모델은 다시 물어도 같은 답을 낸다(실측: 세 번 다 같은 문구). 그러니
   * 되묻기 전에 그 스킬을 목록에서 뺀다. 스킬은 지름길이지 관문이 아니다 — 이 뜻은
   * 아래 완화 단계에 이미 적혀 있었고, 여기에만 없었다.
   */
  let demanded = options.context?.skills ?? [];
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const repair = validation
      ? `\n\n이전 계획은 검증에 실패했다: ${validation}\n같은 오류를 반복하지 말고 화면·표·requiredColumns를 다시 골라 JSON 전체를 새로 출력하라.`
      : '';
    try {
      let plan = parsePlan(
        await chat(prompt + repair, { step: 'plan', ...uploads }), screens, { context },
      );
      // **쓰기는 내민 목록 안에서만 한다.**
      //
      // 프롬프트는 「아래 목록에 있는 화면만 쓴다」고 말하는데 아무도 강제하지 않았다 —
      // `parsePlan` 의 검증은 **전체 카탈로그**(282개)에 있는 이름인지만 본다. 그래서
      // 매입등록을 시켰을 때 목록에 없던 **입고등록**으로 가서 거기에 문서를 만들려
      // 했다(실측 2026-09-15). 기대 화면은 목록에 있었다.
      //
      // 조회는 그대로 둔다 — 틀리면 다시 조회하면 그만이고, 검색이 목록 밖에서 맞는
      // 화면을 찾아 주는 길도 있다. **쓰기는 못 되돌린다.** 엉뚱한 화면에 문서를 만들거나
      // 남의 문서를 덮으면 돌려놓을 값이 아무 데도 없다. 목록에 없으면 다시 계획하게 한다
      // (이 문구를 `index.ts` 의 재계획 갈래가 알아본다).
      offListWrite(plan, shortlist);
      // **빈 상세행은 걷어낸다.** 사용자가 품목을 말하지 않았는데 규칙을 통과하려고
      // 지어낸 자리다 — 두면 런타임이 그 빈 칸을 채우려고 되묻고 문서는 안 만들어진다.
      dropEmptyRows(plan);
      // 상대 날짜는 결정적이라 모델을 기다리지 않고 여기서 바로 고친다. 이렇게 하면
      // 날짜가 실패 축에서 빠지고 모델은 상세행 같은 실제로 판단이 필요한 것만 맞추면 된다.
      plan = repairSpokenDay(plan, question, today, screens);
      validateQuestionCoverage(selectionQuestion, plan, screens, options.context);
      validateSkillCoverage(selectionQuestion, plan, demanded, screens, options.spoken ?? selectionQuestion);
      // 뽑히지 않은 스킬이라도 **그 화면의** 상세행 계약은 지킨다.
      requireDraftRows(plan, options.context?.allSkills ?? []);
      requireEdit(plan, options.context?.allSkills ?? [], options.context?.continuing);
      requireFields(plan, options.context?.allSkills ?? [], options.context?.continuing);
      requireSpokenRows(plan, selectionQuestion);
      requireSpokenCount(plan, selectionQuestion);
      requireNamesNotCodes(plan, screens, selectionQuestion, options.codeLists);
      // 상대 날짜는 **이번 발화**의 말만 본다. 지난 턴의 `내일` 은 그때 이미 날짜로
      // 옮겨졌고, 그것을 다시 재면 멀쩡한 후속 발화가 막힌다.
      requireSpokenDay(plan, question, today);
      // 업무 도구도 capability 로 뽑힌다 — 스킬과 같은 이유로 틀린 도구가 붙는다.
      // 「등록된 비용 항목 보여줘」에 매입 분석 도구의 계약이 매입현황을 요구해, 모델이
      // 맞게 고른 비용항목등록을 거절하느라 **계획 8번·188k 자**를 태웠다(2026-09-23).
      const tool = options.context?.capability;
      // 화면 이름을 **통째로** 말했으면(`namedScreen`) 그 화면이 답이다 — 「이번 달 매출 등록된 거
      // 보여줘」는 매출등록을 말했는데, 매출 도구가 매출현황을 요구했고 둘의 핵심 구절이 같은
      // `매출` 이라 `spokeOtherScreen` 이 「둘 다 말했다」로 셌다. 계획 세 번 끝에 「화면을 찾지
      // 못했습니다」로 끝났다(실제 테넌트, 2026-10-05). 런타임은 같은 이름으로 그 화면에 다시
      // 계획하게 하므로(`계획 다시(말한 화면)`) 여기서 거절하면 둘이 서로를 막는다.
      const wholeName = namedScreen(options.spoken ?? selectionQuestion, screens);
      const spokeWholeScreen = Boolean(tool && wholeName && plan.kind === 'retrieve'
        && plan.retrievals.some((one) => one.route === wholeName.route)
        && !tool.targets.some((one) => one.route === wholeName.route));
      // 스킬과 같은 까닭으로, 계획이 도구의 화면을 하나도 안 쓰고 말에도 그 도구의 근거가 없으면 비킨다.
      // 「내가 상신한 결재 문서」에 통합문서 도구가 붙어 맞게 고른 상신함을 거절하고, 끝내 통합문서로
      // 답했다(실제 테넌트, 2026-10-05). 근거는 도구 화면의 이름과, 그 capability 를 가진 스킬의 트리거다.
      // 화면 이름을 통째로 말했는데 계획이 그 화면을 안 썼으면 비키지 않는다 — 「매출 등록된 거」에 모델이
      // 월매출매입현황을 골랐고, 근거 없음으로 비켜 주자 매출등록도 매출현황도 아닌 답이 나갔다(2026-10-05).
      const toolGrounded = !tool || plan.kind !== 'retrieve'
        || plan.retrievals.some((one) => tool.targets.some((target) => target.route === one.route))
        || Boolean(wholeName && !plan.retrievals.some((one) => one.route === wholeName.route))
        || spokenGrounds(
          options.spoken ?? selectionQuestion,
          (options.context?.skills ?? []).filter((skill) => skill.capabilities?.includes(tool.id))
            .flatMap((skill) => skill.triggers ?? []),
          tool.targets.map((one) => one.route),
          screens,
        );
      // 계획의 화면이 모두 사용자가 말한 업무 용어의 원본 화면이면 비킨다. 「자체점검 중대 이슈」에 과제가 점검 현황 도구를 붙였고,
      // 그 계약이 중대결함 화면 계획을 두 번 거절해 1턴이 계획 6번·156초가 됐다(실제 테넌트, 2026-10-27). 용어가 더 좁게 가리킨다.
      const termScreen = Boolean(tool && plan.kind === 'retrieve' && plan.retrievals.length
        && plan.retrievals.every((one) => (options.context?.termRoutes ?? []).includes(one.route))
        && !plan.retrievals.some((one) => tool.targets.some((target) => target.route === one.route)));
      if (tool && plan.kind === 'retrieve' && (spokeWholeScreen || !toolGrounded || termScreen
        || spokeOtherScreen(options.spoken ?? selectionQuestion, plan, tool.targets.map((one) => one.route), screens))) {
        // eslint-disable-next-line no-console
        console.debug(termScreen ? '[agent] 업무 도구 계약 비킴: 말한 업무 용어의 화면이다' : toolGrounded
          ? '[agent] 업무 도구 계약 비킴 — 질문이 화면을 이름으로 말했다'
          : '[agent] 업무 도구 계약 비킴 — 말에 근거가 없다', tool.id,
          plan.retrievals.map((one) => one.route).join(','));
      } else {
        validateCapabilityPlan(tool, plan);
      }
      return plan;
    } catch (error) {
      // **다시 물어도 안 풀리는 실패는 그 자리에서 내보낸다.**
      //
      // 이 `catch` 는 모든 실패를 「검증 실패」로 보고 되묻는다. 그래서 하루 한도를
      // 다 쓴 429 에도 계획 프롬프트(18k 자 넘는다)를 **세 번 더** 보냈다 — 한 번
      // 실패할 것을 세 번 태우고, 한도가 빠듯할 때 남은 여유를 스스로 깎는다
      // (2026-09-21 실측: 429 하나에 `되물음 1번째`·`2번째`가 잇따랐다).
      //
      // 취소도 같다. 사람이 멈추라고 눌렀는데 세 번 더 부르면 안 된다.
      if (hopeless(error)) throw error;
      lastError = error;
      validation = error instanceof Error ? error.message : String(error);
      // **왜 다시 물었는지 남긴다.** 한 번 걸릴 때마다 계획 프롬프트(18k 자)가 통째로
      // 다시 간다 — 「거래처 몇 곳이야?」 같은 단순한 물음에도 세 번 불렀다(2026-09-21).
      // 어느 검증기가 걷어찼는지 모르면 줄일 수가 없다.
      // 「그 스킬의 권장 화면을 안 썼다」로 걸린 것이면, 다시 묻지 말고 그 스킬을 뺀다.
      const refused = demanded.find((skill) => validation.startsWith(`${skill.name} 스킬의 권장 화면`));
      if (refused) demanded = demanded.filter((skill) => skill !== refused);
      // eslint-disable-next-line no-console
      console.debug('[agent] 계획 되물음', `${attempt + 1}번째`,
        refused ? `${refused.name} 내려놓음 — ` : '', validation.slice(0, 160));
    }
  }

  // 검증을 두 번 못 맞췄다. 그렇다고 아무 답도 주지 않는 것이 가장 나쁘다 —
  // `올해 매출 성장세는 어때?` 가 "사업장별 매출 스킬의 권장 화면을 쓰지 않았다" 로
  // 끝났다. 스킬은 지름길이지 관문이 아니다. 마지막 한 번은 검증을 풀고 계획한다.
  // 실행 자체가 불가능한 계획(없는 화면·표)은 parsePlan 이 여전히 막는다.
  try {
    const loosened = await chat(`${prompt}

이전 계획들이 검증에 실패했다: ${validation}
권장 화면에 매이지 말고, 질문에 답할 수 있는 화면과 컬럼으로 JSON 전체를 새로 출력하라.`, { step: 'plan', ...uploads });
    const plan = parsePlan(loosened, screens, { context });
    dropEmptyRows(plan);
    // 완화는 "권장 화면에 매이지 마라" 까지다. 상세행은 **사용자가 품목을 말했을 때**의
    // 저장 조건이다 — 말하지 않았으면 머리만으로도 저장된다(견적서에서 실측).
    requireDraftRows(plan, options.context?.skills ?? []);
    requireDraftRows(plan, options.context?.allSkills ?? []);
    requireEdit(plan, options.context?.allSkills ?? [], options.context?.continuing);
    requireFields(plan, options.context?.allSkills ?? [], options.context?.continuing);
    // 이 검증만은 풀지 않는다. 스킬을 못 맞춘 계획은 답이 부실할 뿐이지만, 지어낸
    // 품목은 **사람이 주문한 적 없는 문서**가 되어 서버에 남는다.
    requireSpokenRows(plan, selectionQuestion);
    requireSpokenCount(plan, selectionQuestion);
    // 여기서 또 거절하면 사용자는 `요청을 처리하지 못했습니다` 만 받는다. 그 날이
    // 무엇인지는 우리가 안다 — 고쳐서 진행한다.
    const dated = repairSpokenDay(plan, question, today, screens);
    return dated.kind === 'retrieve' ? { ...dated, relaxed: validation } : dated;
  } catch {
    throw lastError;
  }
}
